import { defineStore } from "pinia";
import { defaultTemplate } from "@/store/defaultTemplate";
import setInitState from "@/init/setInitState";
import { IPluginOptions } from "@/schemas/pluginOptions/pluginOptionsSchema";
import { useGeneralStore } from "./generalStore";
import axios, { AxiosResponse } from "axios";
import { api, lockedApi } from "@/api";
import shortid from "shortid";
import {
  TopolTag,
  TopolTemplate,
  TopolSection,
  TopolColumn,
  TopolBlock,
  TopolTranslation,
} from "@/schemas/templateSchema";
import {
  TopolTranslatableBlock,
  isTranslatableAttribute,
  ITranslatableBlockTag,
  TranslationAttributes,
  TopolSpecialBlockTranslation,
  translatableBlockAttributes,
  translatableBlockProps,
  isTranslatableChildAttribute,
  isTranslatableProp,
  TranslationChildAttributes,
  translatableBlockChildAttributes,
  TopolLanguages,
  isAiTranslatableField,
  TranslationUnit,
  TranslationUnitTarget,
  isTranslatableFeedItemField,
} from "@/schemas/template/languageSchema";
import { IMergeTag } from "@/schemas/mergeTags/mergeTagsSchema";
import resolveBlock from "@/store/resolveBlock";
import formatPrice from "@/utils/formatPrice";
import { format } from "date-fns";
import {
  MjmlState,
  MjmlStateGetters,
  MjmlStateActions,
  AuthorizeResponse,
  TopolBlockProps,
  TopolTagProps,
  ResetableValue,
} from "../../types/Mjml/IMjmlStore";
import { i18n } from "@/init/i18n-setup";

import { useThrottleFn } from "@vueuse/core";
import { nextTick } from "vue";

import { useOpenAiStore } from "./openAiStore";
import { useSectionVariantsStore } from "./sectionVariantsStore";
import { useMergeTagsStore } from "./mergeTags";
import { useCustomBlocksStore } from "./customBlocksStore";
import { useApiBlocksStore } from "./apiBlocks";
import { useDefaultsStore } from "./defaultsStore";
import { useNotificationsStore } from "./notifications";
import {
  useSyncedTranslationsStore,
  buildSectionTranslationForSave,
  remapTranslationUids,
} from "./syncedTranslationsStore";

import { validateAndFixMjmlByZod } from "@/utils/validateAndFixMjmlByZod";
import {
  canResizePair,
  clampColumnPercent,
  getAvailablePercent,
  getColumnResizeBounds,
  getSectionContentWidth,
} from "@/utils/columnResizeBounds";
import { usePlanTypeStore } from "./planTypeStore";
import { TopolLoopColumn } from "@/schemas/template/blocks/loopBlockSchema";
import {
  isMjml3,
  convertMjml3toMjml4,
  getEmailPrimaryBlockTranslation,
  addEmailSpecialBlockTranslation,
  emailTranslatableBlockTags,
  getFeedItemsForLang,
  mergeItemAtSlot,
} from "@topol/utils";
import { TopolFeedBlock } from "@/schemas/template/blocks/feedBlockSchema";
import { templateSettings } from "@/store/templateSetting";
import {
  resolvePreviousDefault,
  updateNodesRecursively,
  wasAttrChanged,
} from "@/utils/spacingUtils";
import { templateStyle } from "@/store/templateStyle";

function getSimplifiedOptions(options: IPluginOptions) {
  const simplifiedOptions = {
    ...options,
  };
  delete simplifiedOptions.savedBlocks;
  delete simplifiedOptions.premadeBlocks;
  delete simplifiedOptions.customFonts;
  //@ts-expect-error
  delete simplifiedOptions.authorize;
  //@ts-expect-error
  delete simplifiedOptions.callbacks;
  delete simplifiedOptions.customBlocks;
  delete simplifiedOptions.mergeTags;
  delete simplifiedOptions.fontSizes;
  delete simplifiedOptions.apiAuthorizationHeader;

  return simplifiedOptions;
}

function langsToMutations(langs: TopolLanguages | undefined) {
  return (
    langs?.map((l) => ({
      lang: l.key,
      primary: l.primary,
    })) || []
  );
}

export class AuthorizationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AuthorizationError";
  }
}

export const useMjmlStore = defineStore<
  "mjml",
  MjmlState,
  MjmlStateGetters,
  MjmlStateActions
>("mjml", {
  state: (): MjmlState => ({
    //essential loader
    initialLoaded: false,

    // mobile first
    isMobileView: false,
    isMobileFirstEnabled: false,
    blurHiddenElements: true,

    // mjml
    selectedSection: null,
    selectedBlock: undefined,
    mjml: JSON.parse(JSON.stringify(defaultTemplate())),
    edittedWithoutSave: false,
    redoSnapshots: [],
    snapshots: [],
    redoShowLangSnapshots: [],
    showLangSnapshots: [],
    langToShow: "",
    defaultTemplate: defaultTemplate(templateSettings),

    // general essentials
    authorize: {},
    templateId: "",

    // product
    productTab: "image",

    // TODO:maybe products, maybe feeds
    feeds: [],
    datafeeds: [],

    simulateHoverToUid: null,
    sectionReordering: false,

    //wtf?
    defaultSectionProps: {
      layout: 1,
      backgroundColor: "",
      backgroundImage: "",
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 0,
    },
  }),

  getters: {
    getBase(): any {
      if (this.mjml && this.mjml.children && this.mjml.children[0]) {
        return this.mjml.children[0];
      }
    },

    tagsWithColumnChildren(): any {
      return ["mj-loop", "mj-feed-block"];
    },

    getActiveSectionAttrString: (state) => (prop: any) => {
      const section = state.getSelectedSectionByIndex();
      if (section && section.attributes) {
        return section.attributes[prop];
      }
    },

    getSectionIndexByUid: (state) => (uid: any) => {
      const base = state.getBase;
      const thisSection = base.children.find((item: any) => item.uid === uid);
      return base.children.indexOf(thisSection);
    },

    getSelectedSection(): any {
      const base = this.getBase;
      if (
        base &&
        base.children &&
        this.selectedBlock &&
        this.selectedBlock.sectionIndex != undefined &&
        base.children[this.selectedBlock.sectionIndex]
      ) {
        return base.children[this.selectedBlock.sectionIndex];
      }
    },

    getSelectedSectionByIndex:
      (state) =>
      (index = null) => {
        const base = state.getBase;
        if (index !== null && base.children[index]) {
          return base.children[index];
        }
        if (
          base &&
          base.children &&
          state.selectedSection &&
          state.selectedSection.sectionIndex != undefined &&
          base.children[state.selectedSection.sectionIndex]
        ) {
          return base.children[state.selectedSection.sectionIndex];
        }
      },

    getBlockByIndexes: (state) => (indexes: any) => {
      const base = state.getBase;
      if (base && base.children && base.children[indexes.sectionIndex]) {
        const section = base.children[indexes.sectionIndex];
        if (section.children && section.children[indexes.columnIndex]) {
          const column = section.children[indexes.columnIndex];
          if (column.children && column.children[indexes.blockIndex]) {
            return column.children[indexes.blockIndex];
          }
        }
      }
    },

    getSegmentByUid: (state) => (uid: any) => {
      const base = state.getBase;
      if (!base) {
        return null;
      }
      for (const section of base.children) {
        if (section.uid === uid) {
          return section;
        }
        for (const column of section.children) {
          if (column.uid === uid) {
            return column;
          }
          for (const block of column.children) {
            if (block.uid === uid) {
              return block;
            }
          }
        }
      }
    },

    getSelectedColumn() {
      const section = this.getSelectedSection;
      if (
        section &&
        section.children &&
        this.selectedBlock &&
        this.selectedBlock.columnIndex != undefined &&
        section.children[this.selectedBlock.columnIndex]
      ) {
        return section.children[this.selectedBlock.columnIndex];
      }
    },

    getSelectedBlock() {
      const column = this.getSelectedColumn;
      if (
        column &&
        column.children &&
        this.selectedBlock &&
        this.selectedBlock.blockIndex != undefined &&
        column.children[this.selectedBlock.blockIndex]
      ) {
        return column.children[this.selectedBlock.blockIndex];
      }
    },

    getBlockIndexesByUid: (state) => (uid: any) => {
      const base = state.getBase;
      const indexes = {
        sectionIndex: -1,
        columnIndex: -1,
        blockIndex: -1,
      };

      for (const section of base.children) {
        for (const column of section.children) {
          const thisBlock = column.children.find(
            (item: any) => item.uid === uid
          );
          if (thisBlock) {
            indexes.blockIndex = column.children.indexOf(thisBlock);
            indexes.columnIndex = section.children.indexOf(column);
            indexes.sectionIndex = base.children.indexOf(section);
          }
        }
      }

      return indexes;
    },

    getEdittedWithoutSave: function (state) {
      return state.edittedWithoutSave;
    },

    getProductTab: function (state) {
      return state.productTab;
    },

    getActiveBlock() {
      const block = this.getSelectedBlock;
      if (block) {
        return block;
      }
    },

    getActiveBlockPropInt: (state) => (prop) => {
      //@ts-expect-error
      const block = state.getSelectedBlock;
      if (block) {
        return parseInt(block[prop]);
      }
    },

    getActiveBlockAttrInt: (state) => (prop: any) => {
      const block = state.getSelectedBlock;
      if (block && block.attributes && block.attributes[prop] !== undefined) {
        return Math.round(parseFloat(block.attributes[prop]));
      }
    },

    getActiveBlockAttrString: (state) => (prop: any) => {
      const block = state.getSelectedBlock;
      if (!block) {
        return;
      }

      const mjml = state.mjml as TopolTemplate;
      if (
        isTranslatableAttribute(prop, block.tagName) &&
        state.langToShow &&
        mjml.langs &&
        mjml.langs.length > 0
      ) {
        const translation = mjml.langs.find(
          (lang) => lang.key === state.langToShow
        );
        if (
          translation &&
          translation.translations[block.uid] &&
          translation.translations[block.uid].attributes
        ) {
          const attrs = translation.translations[block.uid].attributes;
          if (attrs && attrs[prop as keyof typeof attrs]) {
            return attrs[prop as keyof typeof attrs];
          }
        }
        let defaultLang = mjml.langs.find((lang) => lang.primary);
        if (!defaultLang) {
          mjml.langs[0].primary = true;
          defaultLang = mjml.langs[0];
          state.emitLanguageCallback("onPrimaryLanguageChanged", [
            mjml.langs[0].key,
            langsToMutations(mjml.langs),
          ]);
        }
        if (
          defaultLang.translations[block.uid] &&
          defaultLang.translations[block.uid].attributes
        ) {
          const attrs = defaultLang.translations[block.uid].attributes;
          if (attrs && attrs[prop as keyof typeof attrs]) {
            return attrs[prop as keyof typeof attrs];
          }
        }
        return "";
      }

      if (block.attributes) {
        return block.attributes[prop];
      }
    },

    getActiveBlockAttrWithDefault:
      (state) =>
      (prop: string): ResetableValue | null => {
        const block = state.getSelectedBlock;
        if (!block || !block.attributes) {
          return null;
        }

        const value = block.attributes[prop];
        const tagName = block.tagName as keyof typeof templateSettings;
        const blockDefaults = templateSettings[tagName];
        let defaultValue: any = undefined;

        if (
          blockDefaults &&
          typeof blockDefaults === "object" &&
          prop in blockDefaults
        ) {
          defaultValue = (blockDefaults as Record<string, any>)[prop];
        }

        // Also check base attributes (mjml.attributes) as they may override templateSettings
        const baseAttr = state.mjml.attributes?.[tagName];
        if (
          baseAttr &&
          typeof baseAttr === "object" &&
          prop in (baseAttr as Record<string, any>)
        ) {
          defaultValue = (baseAttr as Record<string, any>)[prop];
        }

        return {
          value,
          wasChanged: wasAttrChanged(value, defaultValue),
          defaultValue,
        };
      },

    getActiveBlockPropString: (state) => (prop: any) => {
      const block = state.getSelectedBlock;
      if (!block) {
        return "";
      }
      const mjml = state.mjml as TopolTemplate;
      // Top-level translatable props (e.g. mj-rating questionText) — read
      // from current lang's translation with primary fallback.
      if (
        state.langToShow &&
        mjml.langs &&
        mjml.langs.length > 0 &&
        isTranslatableProp(prop, block.tagName)
      ) {
        const translation = mjml.langs.find(
          (lang) => lang.key === state.langToShow
        );
        const blockTrans = translation?.translations?.[block.uid] as any;
        if (
          blockTrans &&
          blockTrans[prop] !== undefined &&
          blockTrans[prop] !== ""
        ) {
          return blockTrans[prop];
        }
        const primary = mjml.langs.find((l) => l.primary);
        const primaryTrans = primary?.translations?.[block.uid] as any;
        if (
          primaryTrans &&
          primaryTrans[prop] !== undefined &&
          primaryTrans[prop] !== ""
        ) {
          return primaryTrans[prop];
        }
        return (block as any)[prop] || "";
      }
      if (
        state.langToShow &&
        mjml.langs &&
        mjml.langs.length > 0 &&
        prop === "content"
      ) {
        const translation = mjml.langs.find(
          (lang) => lang.key === state.langToShow
        );
        if (
          translation &&
          translation.translations[block.uid] &&
          translation.translations[block.uid].content
        ) {
          return translation.translations[block.uid].content;
        }
        let defaultLang = mjml.langs.find((lang) => lang.primary);
        if (!defaultLang) {
          mjml.langs[0].primary = true;
          defaultLang = mjml.langs[0];
          state.emitLanguageCallback("onPrimaryLanguageChanged", [
            mjml.langs[0].key,
            langsToMutations(mjml.langs),
          ]);
        }
        if (
          defaultLang.translations[block.uid] &&
          defaultLang.translations[block.uid].content
        ) {
          return defaultLang.translations[block.uid].content;
        }
        return "";
      }
      return block[prop];
    },

    // For mj-feed-block: return items merged with the current language's
    // overrides (by item.id). Falls back to primary `block.items` when no
    // translation applies.
    getActiveBlockFeedItems: (state) => (block: any) =>
      getFeedItemsForLang(block, state.mjml.langs, state.langToShow),

    getActiveBlockPropDisabled: (state) => (prop: any) => {
      const block = state.getSelectedBlock;

      if (!block || !block.customKey) {
        return false;
      }

      const key = block.customKey;
      const customBlocks = useCustomBlocksStore().customBlocks;
      const thisCustomBlock = customBlocks.find((item) => item.key === key);

      if (!thisCustomBlock) {
        return false;
      }

      if (thisCustomBlock.type !== "mix") {
        if (
          thisCustomBlock.attributes &&
          typeof thisCustomBlock.attributes === "object" &&
          typeof thisCustomBlock.attributes[prop] === "object" &&
          thisCustomBlock.attributes[prop].disabled
        ) {
          return true;
        }
      }

      if (thisCustomBlock.type === "mix" && thisCustomBlock.blocks) {
        const tagName = block.tagName.replace("mj-", "");
        const type =
          tagName !== "image" ? tagName : block.isVideo ? "video" : "image";

        const thisBlock = thisCustomBlock.blocks.find(
          (item) => item.type === type
        );

        if (thisBlock && thisBlock.attributes && thisBlock.attributes[prop]) {
          if (
            typeof thisBlock.attributes[prop] === "object" &&
            thisBlock.attributes[prop].disabled
          ) {
            return true;
          }
        }
      }

      return false;
    },

    // maybe in zoid
    // getActiveBlockPropIsValid: (state) => (prop: any) => {
    //   const block = state.getSelectedBlock;

    //   if (!block && !block.customKey) {
    //     return {
    //       valid: true,
    //     };
    //   }

    //   const key = block.customKey;
    //   const customBlocks = useCustomBlocksStore().customBlocks;
    //   const thisCustomBlock = customBlocks.find((item) => item.key === key);

    //   if (!thisCustomBlock) {
    //     return {
    //       valid: true,
    //     };
    //   }

    //   if (thisCustomBlock.type !== "mix" && thisCustomBlock.attributes) {
    //     if (
    //       typeof thisCustomBlock.attributes[prop] === "object" &&
    //       thisCustomBlock.attributes[prop].validator
    //     ) {
    //       return {
    //         valid: thisCustomBlock.attributes[prop].validator(
    //           block.attributes[prop]
    //         ),
    //         message: thisCustomBlock.attributes[prop].validatorErrorMessage,
    //       };
    //     }
    //   }

    //   if (thisCustomBlock.type === "mix" && thisCustomBlock.blocks) {
    //     const tagName = block.tagName.replace("mj-", "");
    //     const type =
    //       tagName !== "image" ? tagName : block.isVideo ? "video" : "image";

    //     const thisBlock = thisCustomBlock.blocks.find(
    //       (item) => item.type === type
    //     );

    //     if (thisBlock && thisBlock.attributes) {
    //       if (
    //         typeof thisBlock.attributes[prop] === "object" &&
    //         thisBlock.attributes[prop].validator
    //       ) {
    //         return {
    //           valid: thisBlock.attributes[prop].validator(
    //             block.attributes[prop]
    //           ),
    //           message: thisBlock.attributes[prop].validatorErrorMessage,
    //         };
    //       }
    //     }
    //   }

    //   return {
    //     valid: true,
    //   };
    // },
    getSpecificBlockContent: (state) => (block: any, sourceLang?: string) => {
      const mjml = state.mjml as TopolTemplate;
      const langKey = sourceLang ?? state.langToShow;
      if (langKey && mjml.langs && mjml.langs.length > 0) {
        const translation = mjml.langs.find((lang) => lang.key === langKey);
        if (
          translation &&
          translation.translations[block.uid] &&
          translation.translations[block.uid].content
        ) {
          return translation.translations[block.uid].content;
        }
        let defaultLang = mjml.langs.find((lang) => lang.primary);
        if (!defaultLang) {
          mjml.langs[0].primary = true;
          defaultLang = mjml.langs[0];
          state.emitLanguageCallback("onPrimaryLanguageChanged", [
            mjml.langs[0].key,
            langsToMutations(mjml.langs),
          ]);
        }
        if (
          defaultLang.translations[block.uid] &&
          defaultLang.translations[block.uid].content
        ) {
          return defaultLang.translations[block.uid].content;
        }
        const defaultContent = state.getDefaultContent(block);
        if (!defaultContent) return "";
        if (defaultLang.translations[block.uid]) {
          defaultLang.translations[block.uid].content = defaultContent;
          return defaultLang.translations[block.uid].content;
        } else {
          defaultLang.translations[block.uid] = {
            content: defaultContent,
          };
          return defaultLang.translations[block.uid].content;
        }
      }
      return block.content;
    },
    getSpecificBlockAttr: (state) => (props: any) => {
      const base = state.getBase;
      if (
        !base ||
        !base.children ||
        props.sectionIndex === undefined ||
        props.columnIndex === undefined ||
        props.blockIndex === undefined ||
        !base.children[props.sectionIndex]
      ) {
        return;
      }
      const section = base.children[props.sectionIndex];
      if (!section.children || !section.children[props.columnIndex]) {
        return;
      }
      const column = section.children[props.columnIndex];
      if (!column.children || !column.children[props.blockIndex]) {
        return;
      }
      const block = column.children[props.blockIndex];

      const mjml = state.mjml as TopolTemplate;
      if (
        isTranslatableAttribute(props.attr, block.tagName) &&
        state.langToShow &&
        mjml.langs &&
        mjml.langs.length > 0
      ) {
        const translation = mjml.langs.find(
          (lang) => lang.key === state.langToShow
        );
        if (
          translation &&
          translation.translations[block.uid] &&
          translation.translations[block.uid].attributes
        ) {
          const attrs = translation.translations[block.uid].attributes;
          if (attrs && attrs[props.attr as keyof typeof attrs]) {
            return attrs[props.attr as keyof typeof attrs];
          }
        }
        let defaultLang = mjml.langs.find((lang) => lang.primary);
        if (!defaultLang) {
          mjml.langs[0].primary = true;
          defaultLang = mjml.langs[0];
          state.emitLanguageCallback("onPrimaryLanguageChanged", [
            mjml.langs[0].key,
            langsToMutations(mjml.langs),
          ]);
        }
        if (
          defaultLang.translations[block.uid] &&
          defaultLang.translations[block.uid].attributes
        ) {
          const attrs = defaultLang.translations[block.uid].attributes;
          if (attrs && attrs[props.attr as keyof typeof attrs]) {
            return attrs[props.attr as keyof typeof attrs];
          }
        }
        return "";
      }

      if (block.attributes) {
        return block.attributes[props.attr];
      }
    },
    getBlockChildrenAttribute: (state) => (childIndex: number, prop: any) => {
      const block = state.getSelectedBlock;
      if (
        !block ||
        !(
          block.tagName === "mj-carousel" ||
          block.tagName === "mj-social" ||
          block.tagName === "mj-rating"
        ) ||
        !block.children ||
        !block.children[childIndex]
      ) {
        return;
      }

      const mjml = state.mjml as TopolTemplate;
      if (
        isTranslatableChildAttribute(prop, block.tagName) &&
        state.langToShow &&
        mjml.langs &&
        mjml.langs.length > 0
      ) {
        const translation = mjml.langs.find(
          (lang) => lang.key === state.langToShow
        );
        if (translation) {
          const children = translation.translations[block.uid]?.children;
          if (children && children[childIndex]?.attributes) {
            const attrs = children[childIndex].attributes;
            if (attrs && attrs[prop as keyof typeof attrs]) {
              return attrs[prop as keyof typeof attrs];
            }
          }
        }
        let defaultLang = mjml.langs.find((lang) => lang.primary);
        if (!defaultLang) {
          mjml.langs[0].primary = true;
          defaultLang = mjml.langs[0];
          state.emitLanguageCallback("onPrimaryLanguageChanged", [
            mjml.langs[0].key,
            langsToMutations(mjml.langs),
          ]);
        }
        const defChildren = defaultLang.translations[block.uid]?.children;
        if (defChildren && defChildren[childIndex]?.attributes) {
          const attrs = defChildren[childIndex].attributes;
          if (attrs && attrs[prop as keyof typeof attrs]) {
            return attrs[prop as keyof typeof attrs];
          }
        }
        return "";
      }

      if (block.children[childIndex]?.attributes) {
        return block.children[childIndex].attributes[prop];
      }
    },
    // Block-aware variants of the active-block getters above. The canvas
    // (mjml/*.vue) renders blocks via `props.block` rather than the selected
    // one, so it can't use getActiveBlockPropString / getBlockChildrenAttribute
    // (which rely on getSelectedBlock).
    getSpecificBlockProp: (state) => (block: any, prop: string) => {
      if (!block) return "";
      const mjml = state.mjml as TopolTemplate;
      if (
        state.langToShow &&
        mjml.langs &&
        mjml.langs.length > 0 &&
        isTranslatableProp(prop, block.tagName)
      ) {
        const trans = mjml.langs.find((l) => l.key === state.langToShow)
          ?.translations?.[block.uid] as any;
        if (trans && trans[prop] !== undefined && trans[prop] !== "") {
          return trans[prop];
        }
        const primary = mjml.langs.find((l) => l.primary)?.translations?.[
          block.uid
        ] as any;
        if (primary && primary[prop] !== undefined && primary[prop] !== "") {
          return primary[prop];
        }
      }
      return block[prop] ?? "";
    },
    getSpecificBlockChildAttr:
      (state) => (block: any, childIndex: number, attr: string) => {
        if (!block?.children?.[childIndex]) return "";
        const mjml = state.mjml as TopolTemplate;
        if (
          state.langToShow &&
          mjml.langs &&
          mjml.langs.length > 0 &&
          isTranslatableChildAttribute(attr, block.tagName)
        ) {
          const trans = mjml.langs.find((l) => l.key === state.langToShow)
            ?.translations?.[block.uid] as any;
          const childAttrs = trans?.children?.[childIndex]?.attributes;
          if (childAttrs && childAttrs[attr]) return childAttrs[attr];
          const primary = mjml.langs.find((l) => l.primary)?.translations?.[
            block.uid
          ] as any;
          const primaryChildAttrs = primary?.children?.[childIndex]?.attributes;
          if (primaryChildAttrs && primaryChildAttrs[attr])
            return primaryChildAttrs[attr];
        }
        return block.children[childIndex]?.attributes?.[attr] ?? "";
      },
    isBlockChildrenAttributeLangDiff:
      (state) => (childIndex: number, prop: any) => {
        const block = state.getSelectedBlock;
        if (
          !block ||
          !(
            block.tagName === "mj-carousel" ||
            block.tagName === "mj-social" ||
            block.tagName === "mj-rating"
          ) ||
          !block.children ||
          !block.children[childIndex]
        ) {
          return false;
        }

        const mjml = state.mjml as TopolTemplate;
        if (
          isTranslatableChildAttribute(prop, block.tagName) &&
          mjml.langs &&
          mjml.langs.length > 0
        ) {
          let val = "";
          for (let i = 0; i < mjml.langs.length; i++) {
            const lang = mjml.langs[i].translations[block.uid];
            if (lang && lang.children && lang.children[childIndex]) {
              const attrs = lang.children[childIndex].attributes;
              const langVal = attrs?.[prop as keyof typeof attrs];

              if (langVal && langVal !== "") {
                if (i === 0) {
                  val = String(langVal);
                } else if (String(langVal) !== val) {
                  return true;
                }
              } else if (mjml.langs[i].primary) {
                return true;
              }
            } else if (mjml.langs[i].primary) {
              return true;
            }
          }
        }
        return false;
      },

    getActiveSectionAttrInt: (state) => (prop: any) => {
      const section = state.getSelectedSectionByIndex();
      if (section && section.attributes) {
        return parseInt(section.attributes[prop]);
      }
    },

    getActiveSectionType(): string {
      const section = this.getSelectedSectionByIndex();
      if (section && section.type) {
        return section.type;
      } else {
        return "";
      }
    },

    getActiveSectionColumns(): any {
      const section = this.getSelectedSectionByIndex();

      if (section && section.children) {
        return section.children;
      }
    },

    getActiveSectionColumnsCount(): any {
      const section = this.getSelectedSectionByIndex();

      if (section && section.children) {
        return section.children.length;
      }
    },

    getActiveBlockColumnsCount(): any {
      const block = this.getSelectedBlock;

      if (
        block &&
        this.tagsWithColumnChildren.includes(block.tagName) &&
        "children" in block
      ) {
        return block.children.length;
      }
    },

    getBodyBg() {
      const base = this.getBase;
      if (base) {
        return base.attributes["background-color"];
      }
    },

    getBodyWidth() {
      const base = this.getBase;
      if (base) {
        return base.attributes["containerWidth"];
      } else return 600;
    },

    getBaseAttr: (state) => (block: any, prop: any) => {
      if (!state.mjml.attributes) {
        return;
      }

      if (state.mjml.attributes && !state.mjml.attributes[block]) {
        return;
      }

      return state.mjml.attributes[block][prop];
    },

    getBaseAttrString: (state) => (block: any, prop: any) => {
      if (!state.mjml.attributes || !state.mjml.attributes[block]) {
        return "";
      }

      return state.mjml.attributes[block][prop];
    },

    getBaseAttrWithDefault:
      (state) =>
      (block: string, prop: string): ResetableValue => {
        const value =
          state.mjml.attributes?.[
            block as keyof typeof state.mjml.attributes
          ]?.[prop as any] ?? undefined;
        const blockDefaults =
          templateSettings[block as keyof typeof templateSettings];
        let defaultValue: any = undefined;

        if (
          blockDefaults &&
          typeof blockDefaults === "object" &&
          prop in blockDefaults
        ) {
          defaultValue = (blockDefaults as Record<string, any>)[prop];
        }

        return {
          value,
          wasChanged: wasAttrChanged(value, defaultValue),
          defaultValue,
        };
      },

    getActiveBlockGroupAttrWithDefault:
      (state) =>
      (group: string, prop: string): ResetableValue | null => {
        const block = (state as any).getSelectedBlock;
        if (!block) return null;

        const value = block[group]?.[prop];
        const tagName = block.tagName as keyof typeof templateSettings;
        const blockDefaults = templateSettings[tagName];
        let defaultValue: any = undefined;

        if (
          blockDefaults &&
          typeof blockDefaults === "object" &&
          group in blockDefaults
        ) {
          const groupDefaults = (blockDefaults as Record<string, any>)[group];
          if (
            groupDefaults &&
            typeof groupDefaults === "object" &&
            prop in groupDefaults
          ) {
            defaultValue = groupDefaults[prop];
          }
        }

        const baseAttr = state.mjml.attributes?.[tagName] as any;
        if (
          baseAttr &&
          typeof baseAttr === "object" &&
          baseAttr[group] &&
          typeof baseAttr[group] === "object" &&
          prop in baseAttr[group]
        ) {
          defaultValue = baseAttr[group][prop];
        }

        return {
          value,
          wasChanged: wasAttrChanged(value, defaultValue),
          defaultValue,
        };
      },

    getBaseGroupAttrWithDefault:
      (state) =>
      (block: string, group: string, prop: string): ResetableValue => {
        const baseAttr = state.mjml.attributes?.[
          block as keyof typeof state.mjml.attributes
        ] as any;
        const value =
          baseAttr && typeof baseAttr === "object" && baseAttr[group]
            ? baseAttr[group][prop]
            : undefined;

        const blockDefaults =
          templateSettings[block as keyof typeof templateSettings];
        let defaultValue: any = undefined;
        if (
          blockDefaults &&
          typeof blockDefaults === "object" &&
          group in blockDefaults
        ) {
          const groupDefaults = (blockDefaults as Record<string, any>)[group];
          if (
            groupDefaults &&
            typeof groupDefaults === "object" &&
            prop in groupDefaults
          ) {
            defaultValue = groupDefaults[prop];
          }
        }

        return {
          value,
          wasChanged: wasAttrChanged(value, defaultValue),
          defaultValue,
        };
      },

    getTranslatableBaseAttr:
      (state) => (block: any, prop: any, sourceLang?: string) => {
        const mjml = state.mjml as TopolTemplate;
        const langKey = sourceLang ?? state.langToShow;
        if (langKey && mjml.langs && mjml.langs.length > 0) {
          const translation = mjml.langs.find((lang) => lang.key === langKey);
          if (
            translation &&
            translation.translations[block] &&
            translation.translations[block].content
          ) {
            return translation.translations[block].content;
          }
          let defaultLang = mjml.langs.find((lang) => lang.primary);
          if (!defaultLang) {
            mjml.langs[0].primary = true;
            defaultLang = mjml.langs[0];
            state.emitLanguageCallback("onPrimaryLanguageChanged", [
              mjml.langs[0].key,
              langsToMutations(mjml.langs),
            ]);
          }
          if (
            defaultLang.translations[block] &&
            defaultLang.translations[block].content
          ) {
            return defaultLang.translations[block].content;
          }
        }
        if (!state.mjml.attributes || !state.mjml.attributes[block]) {
          return "";
        }
        return state.mjml.attributes[block][prop];
      },

    isActiveBlock: (state) => (prop: any) => {
      if (!state.selectedBlock) {
        return false;
      }
      if (
        state.selectedBlock.sectionIndex == prop.sectionIndex &&
        state.selectedBlock.columnIndex == prop.columnIndex &&
        state.selectedBlock.blockIndex == prop.blockIndex
      ) {
        return true;
      }
      return false;
    },

    isActiveSection: (state) => (prop) => {
      if (!state.selectedSection) {
        return false;
      }
      if (state.selectedSection.sectionIndex == prop.sectionIndex) {
        return true;
      }
      return false;
    },

    getStyle: (state) => (tag: TopolTag, attr: string) => {
      const relatedColorTags = ["h1", "h2", "h3", "a"] as const;
      type TagColor = (typeof relatedColorTags)[number];
      let style: string | undefined =
        state.mjml.style?.[tag]?.[
          attr as keyof (typeof state.mjml.style)[typeof tag]
        ];
      if (!style && attr === "color") {
        if (relatedColorTags.includes(tag as TagColor)) {
          style = state.mjml.attributes?.[`${tag as TagColor}:color`];
        } else {
          style = state.mjml.attributes?.[`:color`];
        }
      }
      return style ?? "";
    },

    getStyleWithDefault:
      (state) =>
      (tag: TopolTag, attr: string): ResetableValue => {
        const relatedColorTags = ["h1", "h2", "h3", "a"] as const;
        type TagColor = (typeof relatedColorTags)[number];

        // CSS-neutral defaults for properties not in templateStyle
        const cssNeutralDefaults: Record<string, any> = {
          "font-weight": "normal",
          "font-style": "normal",
          "text-decoration": "none",
          "text-transform": "none",
          "letter-spacing": "0px",
          "line-height": templateSettings[":line-height"]
            ? parseFloat(templateSettings[":line-height"])
            : 1.5,
          "margin-top": "11px",
          "margin-bottom": "11px",
          "padding-left": "40px",
        };

        // Value explicitly set in style
        const styleValue: any =
          state.mjml.style?.[tag]?.[
            attr as keyof (typeof state.mjml.style)[typeof tag]
          ];

        // For color, fall back to attributes as the "initial" value
        let attrFallback: any = undefined;
        if (attr === "color") {
          if (relatedColorTags.includes(tag as TagColor)) {
            attrFallback = state.mjml.attributes?.[`${tag as TagColor}:color`];
          } else {
            attrFallback = state.mjml.attributes?.[`:color`];
          }
        }

        const value = styleValue ?? attrFallback;

        const tagDefaults = templateStyle[tag as keyof typeof templateStyle];
        let defaultValue: any = undefined;

        if (
          tagDefaults &&
          typeof tagDefaults === "object" &&
          attr in tagDefaults
        ) {
          defaultValue = (tagDefaults as Record<string, any>)[attr];
        }

        // Fallback: use attribute color or CSS-neutral default
        if (defaultValue === undefined) {
          if (attr === "color" && attrFallback) {
            defaultValue = attrFallback;
          } else if (attr in cssNeutralDefaults) {
            defaultValue = cssNeutralDefaults[attr];
          }
        }

        return {
          value: value ?? defaultValue,
          wasChanged:
            styleValue !== undefined &&
            defaultValue !== undefined &&
            styleValue !== defaultValue,
          defaultValue,
        };
      },

    getMjml: (state) => () => {
      const usedFonts = <string[]>[];
      const mjml = state.mjml;

      if (state.mjml && state.mjml.children && state.mjml.children[0]) {
        // Sections
        state.mjml.children[0].children.forEach(function (section) {
          if (!section.attributes) {
            section.attributes = {};
          }
          if (!section.attributes["mj-class"])
            section.attributes["mj-class"] = "section";
          // Columns
          if (section.children) {
            section.children.forEach(function (column) {
              if (
                column.attributes &&
                column.attributes["border-radius"] &&
                parseInt(column.attributes["border-radius"]) > 0 &&
                column.attributes["css-class"] !== "rounded"
              ) {
                column.attributes["css-class"] = "rounded";
              }
              // Blocks
              if (column.children) {
                column.children.forEach(function (block) {
                  if (block && block.tagName == "mj-text") {
                    //block.attributes[props.prop] = props.value
                    const font = block.attributes["font-family"];
                    if (font) {
                      if (usedFonts.indexOf(font) == -1) {
                        usedFonts.push(font);
                      }
                    }
                  }
                  if (
                    block &&
                    (block.tagName === "mj-product" ||
                      block.tagName === "mj-article")
                  ) {
                    if (!block.blockName) {
                      // @ts-expect-error
                      block.blockName = block.tagName.replace("mj-", "");
                    }
                    if (!block.attributes["button-inner-padding"]) {
                      block.attributes["button-inner-padding"] =
                        "10px 20px 10px 20px";
                    }
                  }
                });
              }
            });
          }
        });
      }

      if (!mjml.style) {
        // @ts-expect-error
        mjml.style = {};
      }

      if (!mjml.style["h1"]) {
        // @ts-expect-error
        mjml.style["h1"] = {};
      }
      if (!mjml.style["h1"]["font-weight"]) {
        mjml.style["h1"]["font-weight"] = "bold";
      }
      if (
        mjml.style["h1"]["font-family"] &&
        usedFonts.indexOf(mjml.style["h1"]["font-family"]) == -1
      ) {
        usedFonts.push(mjml.style["h1"]["font-family"]);
      }

      if (!mjml.style["h2"]) {
        // @ts-expect-error
        mjml.style["h2"] = {};
      }
      if (!mjml.style["h2"]["font-weight"]) {
        mjml.style["h2"]["font-weight"] = "bold";
      }
      if (
        mjml.style["h2"]["font-family"] &&
        usedFonts.indexOf(mjml.style["h2"]["font-family"]) == -1
      ) {
        usedFonts.push(mjml.style["h2"]["font-family"]);
      }

      if (!mjml.style["h3"]) {
        // @ts-expect-error
        mjml.style["h3"] = {};
      }
      if (!mjml.style["h3"]["font-weight"]) {
        mjml.style["h3"]["font-weight"] = "bold";
      }
      if (
        mjml.style["h3"]["font-family"] &&
        usedFonts.indexOf(mjml.style["h3"]["font-family"]) == -1
      ) {
        usedFonts.push(mjml.style["h3"]["font-family"]);
      }

      if (!mjml.style[".rounded>table"]) {
        mjml.style[".rounded>table"] = {
          "border-collapse": "separate",
        };
      }

      if (mjml.attributes) {
        if (
          mjml.attributes["mj-button"] &&
          mjml.attributes["mj-button"]["font-family"] &&
          usedFonts.indexOf(mjml.attributes["mj-button"]["font-family"]) == -1
        ) {
          usedFonts.push(mjml.attributes["mj-button"]["font-family"]);
        }
        if (
          mjml.attributes["mj-text"] &&
          mjml.attributes["mj-text"]["font-family"] &&
          usedFonts.indexOf(mjml.attributes["mj-text"]["font-family"]) == -1
        ) {
          usedFonts.push(mjml.attributes["mj-text"]["font-family"]);
        }
      }

      const copyMjml = Object.assign({}, mjml);
      copyMjml.fonts = usedFonts;

      return copyMjml;
      /**
             if(usedFonts.length > 0) {
                mjml.fonts = usedFonts;
            }*/
    },

    getOnlyTextAndButtonBlocks:
      (state) =>
      (props?: {
        selectedBlock?: any;
        ignoreSynced?: boolean;
        notifySyncedIgnore?: boolean;
        sourceLang?: string;
      }) => {
        const filteredBlocks: { uid: string; content: string }[] = [];

        const addBlock = (block: any) => {
          if (block.tagName === "mj-text" || block.tagName === "mj-button") {
            filteredBlocks.push({
              uid: block.uid,
              content: state.getSpecificBlockContent(block, props?.sourceLang),
            });
          }
        };

        if (props?.selectedBlock) {
          if (props.selectedBlock.tagName === "mj-section") {
            props.selectedBlock.children.forEach((column: any) => {
              column.children.forEach(addBlock);
            });
          } else {
            addBlock(props.selectedBlock);
          }
          return filteredBlocks;
        }
        const base = state.getBase;
        let wasNotified = false;
        if (base?.children) {
          base.children.forEach((section: any) => {
            if (props?.ignoreSynced && section.syncedId !== undefined) {
              if (props?.notifySyncedIgnore && !wasNotified) {
                useNotificationsStore().createNotification({
                  type: "info",
                  title: i18n.global.t("errors.syncedNotTranslatableTitle"),
                  text: i18n.global.t("errors.syncedNotTranslatableText"),
                });
                wasNotified = true;
              }
              return;
            }
            section.children.forEach((column: any) => {
              column.children.forEach(addBlock);
            });
          });
        }
        return filteredBlocks;
      },
    getTranslatableUnits:
      (state) =>
      (props?: {
        ignoreSynced?: boolean;
        notifySyncedIgnore?: boolean;
        sourceLang?: string;
      }) => {
        const mjml = state.mjml as TopolTemplate;
        const langs = mjml.langs;
        const sourceLang = props?.sourceLang;
        const units: TranslationUnit[] = [];
        let counter = 0;

        const findLang = (key?: string) =>
          key && langs ? langs.find((l) => l.key === key) : undefined;
        const primaryLang = () => langs?.find((l) => l.primary);

        // Source value for an attribute: prefer the source mutation, fall back
        // to the primary mutation, then the block's own (primary) attribute.
        const readAttr = (block: any, attr: string): string => {
          const fromSource = (
            findLang(sourceLang)?.translations?.[block.uid]?.attributes as any
          )?.[attr];
          if (fromSource) return String(fromSource);
          const fromPrimary = (
            primaryLang()?.translations?.[block.uid]?.attributes as any
          )?.[attr];
          if (fromPrimary) return String(fromPrimary);
          return block.attributes?.[attr] ?? "";
        };
        const readProp = (block: any, prop: string): string => {
          const fromSource = (
            findLang(sourceLang)?.translations?.[block.uid] as any
          )?.[prop];
          if (fromSource) return String(fromSource);
          const fromPrimary = (
            primaryLang()?.translations?.[block.uid] as any
          )?.[prop];
          if (fromPrimary) return String(fromPrimary);
          return block[prop] ?? "";
        };
        const readChildAttr = (block: any, i: number, attr: string): string => {
          const fromSource = (
            findLang(sourceLang)?.translations?.[block.uid]?.children?.[i]
              ?.attributes as any
          )?.[attr];
          if (fromSource) return String(fromSource);
          const fromPrimary = (
            primaryLang()?.translations?.[block.uid]?.children?.[i]
              ?.attributes as any
          )?.[attr];
          if (fromPrimary) return String(fromPrimary);
          return block.children?.[i]?.attributes?.[attr] ?? "";
        };

        const pushIf = (content: string, target: TranslationUnitTarget) => {
          if (content && content.trim() !== "") {
            units.push({ id: String(counter++), content, target });
          }
        };

        const addBlock = (block: any) => {
          const tag = block.tagName;

          // Text content (text / button / raw).
          if (tag === "mj-text" || tag === "mj-button" || tag === "mj-raw") {
            pushIf(state.getSpecificBlockContent(block, sourceLang), {
              kind: "content",
              uid: block.uid,
            });
          }

          // Block attributes (alt / title …).
          const attrs =
            translatableBlockAttributes[
              tag as keyof typeof translatableBlockAttributes
            ];
          if (attrs) {
            for (const attr of attrs) {
              if (!isAiTranslatableField(attr)) continue;
              pushIf(readAttr(block, attr), {
                kind: "attr",
                uid: block.uid,
                attr,
              });
            }
          }

          // Top-level props (rating question / captions). `items` (feed) is a
          // per-id override structure handled elsewhere, not flat text.
          const blockProps =
            translatableBlockProps[tag as keyof typeof translatableBlockProps];
          if (blockProps) {
            for (const prop of blockProps) {
              if (prop === "items" || !isAiTranslatableField(prop)) continue;
              pushIf(readProp(block, prop), {
                kind: "prop",
                uid: block.uid,
                prop,
              });
            }
          }

          // Child attributes (social / rating segments).
          const childAttrs =
            translatableBlockChildAttributes[
              tag as keyof typeof translatableBlockChildAttributes
            ];
          if (childAttrs && Array.isArray(block.children)) {
            block.children.forEach((_child: any, i: number) => {
              for (const attr of childAttrs) {
                if (!isAiTranslatableField(attr)) continue;
                pushIf(readChildAttr(block, i, attr), {
                  kind: "childAttr",
                  uid: block.uid,
                  childIndex: i,
                  attr,
                });
              }
            });
          }

          // Feed block (products): translate every text field of each static
          // item (title, description, button, image alt/title, custom text
          // fields …), skipping links, image src, prices and ids. Dynamic feeds
          // are resolved per-recipient from merge tags at send time, so there's
          // nothing meaningful to translate. Source values come from the source
          // mutation merged onto primary; the override key is the PRIMARY item's
          // id, matching the per-id sparse-override storage write-back.
          if (tag === "mj-feed-block" && block.mode !== "dynamic") {
            const primaryItems = getFeedItemsForLang(
              block,
              langs,
              primaryLang()?.key
            );
            const sourceItems = getFeedItemsForLang(block, langs, sourceLang);
            primaryItems.forEach((primaryItem: any, i: number) => {
              const srcItem = sourceItems[i] ?? primaryItem;
              if (!srcItem) return;
              for (const field of Object.keys(srcItem)) {
                if (!isTranslatableFeedItemField(field, srcItem[field])) {
                  continue;
                }
                pushIf(String(srcItem[field]), {
                  kind: "feedItem",
                  uid: block.uid,
                  itemIndex: i,
                  itemId: primaryItem?.id,
                  field,
                });
              }
            });
          }
        };

        const base = state.getBase;
        let wasNotified = false;
        if (base?.children) {
          base.children.forEach((section: any) => {
            if (props?.ignoreSynced && section.syncedId !== undefined) {
              if (props?.notifySyncedIgnore && !wasNotified) {
                useNotificationsStore().createNotification({
                  type: "info",
                  title: i18n.global.t("errors.syncedNotTranslatableTitle"),
                  text: i18n.global.t("errors.syncedNotTranslatableText"),
                });
                wasNotified = true;
              }
              return;
            }
            section.children.forEach((column: any) => {
              column.children.forEach(addBlock);
            });
          });
        }

        // Base attributes: preview text + subject line.
        pushIf(
          state.getTranslatableBaseAttr("mj-preview", "text", sourceLang),
          {
            kind: "baseAttr",
            block: "mj-preview",
          }
        );
        pushIf(
          state.getTranslatableBaseAttr("mj-subject", "text", sourceLang),
          {
            kind: "baseAttr",
            block: "mj-subject",
          }
        );

        return units;
      },
    getTranslatableBlocks() {
      const base = this.getBase;
      if (base && base.children) {
        const translatableBlocks: TopolTranslatableBlock[] = [];
        base.children.forEach((section: TopolSection) => {
          section.children.forEach((column: TopolColumn) => {
            column.children.forEach((block: TopolBlock) => {
              if (
                emailTranslatableBlockTags.includes(
                  block.tagName as ITranslatableBlockTag
                )
              ) {
                translatableBlocks.push(block as TopolTranslatableBlock);
              }
            });
          });
        });
        return translatableBlocks;
      }
      return [];
    },

    getSectionTranslationByIndex: (state) => (index: any) => {
      const base = state.getBase;
      if (!base || !base.children || !base.children[index]) {
        return;
      }
      const section = base.children[index];

      const entry = useSyncedTranslationsStore().getSectionTranslation(
        section.uid
      );
      const preserved: TopolSpecialBlockTranslation = entry?.fullTranslation
        ? JSON.parse(JSON.stringify(entry.fullTranslation))
        : {};

      const blocks: TopolBlock[] = [];
      for (const column of section.children) {
        for (const block of column.children) {
          blocks.push(block);
        }
      }

      return buildSectionTranslationForSave({
        preserved,
        langs: state.mjml.langs,
        blocks,
        pickedLang: entry?.pickedLang,
        isTranslatable: (block) =>
          emailTranslatableBlockTags.includes(
            block.tagName as ITranslatableBlockTag
          ),
        getPrimaryTranslation: getEmailPrimaryBlockTranslation,
      });
    },

    getSyncedSectionsUsage() {
      const base = this.getBase;
      if (!base || !base.children) {
        return [];
      }
      const syncedSectionsUsage: number[] = [];
      base.children.forEach((section: any) => {
        if (section.syncedId) {
          syncedSectionsUsage.push(section.syncedId);
        }
      });
      return syncedSectionsUsage;
    },

    getTranslatedBlocksJson: (state) => () => {
      const mjml = JSON.parse(JSON.stringify(state.mjml)) as TopolTemplate;

      if (state.langToShow && mjml.langs && mjml.langs.length > 0) {
        const primaryLang = mjml.langs.find((lang) => lang.primary);
        if (!primaryLang) {
          return mjml;
        }
        let translation = mjml.langs.find(
          (lang) => lang.key === state.langToShow
        );
        if (!translation) {
          translation = primaryLang;
        }
        for (const section of mjml.children[0].children) {
          for (const column of section.children) {
            for (const block of column.children) {
              if (
                !emailTranslatableBlockTags.includes(
                  block.tagName as ITranslatableBlockTag
                )
              ) {
                continue;
              }
              const primaryBlockTranslation =
                primaryLang.translations[block.uid];
              const blockTranslation = translation.translations[block.uid];
              if (!blockTranslation && !primaryBlockTranslation) {
                continue;
              }
              if (
                block.tagName === "mj-button" ||
                block.tagName === "mj-text"
              ) {
                if (blockTranslation?.content) {
                  block.content = blockTranslation.content;
                } else if (primaryBlockTranslation?.content) {
                  block.content = primaryBlockTranslation.content;
                }
              }
              // Top-level translatable props (e.g. mj-rating questionText)
              const translatableProps =
                translatableBlockProps[
                  block.tagName as keyof typeof translatableBlockProps
                ];
              if (translatableProps) {
                for (const prop of translatableProps) {
                  const t = blockTranslation as any;
                  const p = primaryBlockTranslation as any;
                  if (block.tagName === "mj-feed-block" && prop === "items") {
                    (block as any).items = getFeedItemsForLang(
                      block,
                      mjml.langs,
                      state.langToShow
                    );
                    continue;
                  }
                  if (t?.[prop] !== undefined && t[prop] !== "") {
                    (block as any)[prop] = t[prop];
                  } else if (p?.[prop] !== undefined && p[prop] !== "") {
                    (block as any)[prop] = p[prop];
                  }
                }
              }

              const translatableAttributes =
                translatableBlockAttributes[
                  block.tagName as keyof typeof translatableBlockAttributes
                ];
              if (!translatableAttributes) {
                if (
                  block.tagName === "mj-rating" &&
                  translatableBlockChildAttributes[
                    block.tagName as keyof typeof translatableBlockChildAttributes
                  ]
                ) {
                  // fall through to children handling below
                } else {
                  continue;
                }
              }
              for (const attr of translatableAttributes || []) {
                if (
                  blockTranslation?.attributes?.[
                    attr as keyof typeof blockTranslation.attributes
                  ]
                ) {
                  block.attributes[attr] =
                    blockTranslation.attributes[
                      attr as keyof typeof blockTranslation.attributes
                    ];
                } else if (
                  primaryBlockTranslation?.attributes?.[
                    attr as keyof typeof primaryBlockTranslation.attributes
                  ]
                ) {
                  block.attributes[attr] =
                    primaryBlockTranslation.attributes[
                      attr as keyof typeof primaryBlockTranslation.attributes
                    ];
                }
              }
              if (
                block.tagName === "mj-social" ||
                block.tagName === "mj-rating"
              ) {
                if (block.children) {
                  block.children.forEach((child: any, index: number) => {
                    const primaryChildTranslation =
                      primaryLang.translations[block.uid]?.children?.[index];
                    const childTranslation =
                      translation.translations[block.uid]?.children?.[index];
                    if (!childTranslation && !primaryChildTranslation) {
                      return;
                    }
                    const translatableChildAttributes =
                      translatableBlockChildAttributes[
                        block.tagName as keyof typeof translatableBlockChildAttributes
                      ];
                    if (!translatableChildAttributes) {
                      return;
                    }
                    for (const attr of translatableChildAttributes) {
                      if (
                        childTranslation?.attributes?.[
                          attr as keyof typeof childTranslation.attributes
                        ]
                      ) {
                        child.attributes[attr] =
                          childTranslation.attributes[
                            attr as keyof typeof childTranslation.attributes
                          ];
                      } else if (
                        primaryChildTranslation?.attributes?.[
                          attr as keyof typeof primaryChildTranslation.attributes
                        ]
                      ) {
                        child.attributes[attr] =
                          primaryChildTranslation.attributes[
                            attr as keyof typeof primaryChildTranslation.attributes
                          ];
                      }
                    }
                  });
                }
              }
            }
          }
        }
      }

      return mjml;
    },
  },

  actions: {
    async setInitialLoader(
      options: IPluginOptions,
      hostname: string,
      mode: "production" | "testing" | "development"
    ): Promise<void> {
      await setInitState(options, hostname, mode);

      this.initialLoaded = true;
    },

    async authorizePromise(params) {
      const generalStore = useGeneralStore();

      try {
        const data = await axios.post<AuthorizeResponse>(lockedApi.AUTHORIZE, {
          ...this.authorize,
          options:
            params && params.options
              ? JSON.stringify(getSimplifiedOptions(params.options))
              : undefined,
        });

        const planTypeStore = usePlanTypeStore();
        planTypeStore.setSubscriptionPlan(data.data.subscriptionPlan);

        const openAiStore = useOpenAiStore();
        openAiStore.openAiTokenPresent = data.data.openAiEnabled;

        generalStore.metadata = data.data.metadata;
        generalStore.type = data.data.metadata.mode
          ? data.data.metadata.mode
          : "plugin";

        return data;
      } catch (err) {
        throw new AuthorizationError(String(err));
      }
    },

    load(): Promise<AxiosResponse> {
      return new Promise((resolve, reject) => {
        let loadUrl = api.LOAD;
        if (loadUrl) {
          loadUrl = loadUrl.replace("{key}", this.authorize.apiKey);
          loadUrl = loadUrl.replace("{id}", this.templateId);
          loadUrl = loadUrl.replace("{uuid}", this.authorize.userId);

          axios
            .get(loadUrl, { params: { hostname: this.authorize.hostname } })
            .then(({ data }) => {
              if (data && data.body) {
                // Fix if coming from Lambda endpoint
                if (data.body) {
                  data.body = JSON.parse(data.body);
                }

                this.setMjml(data.body.template);
                resolve(data);
              } else {
                reject();
              }
            })
            .catch((e) => {
              reject(e);
            });
        }
      });
    },

    loadJson(json: string | object) {
      let _json;
      if (typeof json === "string") {
        _json = JSON.parse(json);
      } else {
        _json = json;
      }
      let body: { template?: any; feeds?: any; datafeeds?: any } = {};

      if (!_json.template) {
        body.template = _json;
      } else {
        body = _json;
      }

      this.setMjml(body.template);

      if (body.feeds) {
        this.feeds = body.feeds;
      }

      if (body.datafeeds) {
        this.datafeeds = body.datafeeds;
      }
    },

    setMjml(mjml: TopolTemplate) {
      this.langToShow = "";
      if (!mjml) {
        const message = {
          type: "callback",
          action: "onError",
          data: {
            args: ["templateLoad", "Error loading template"],
          },
        };

        window.parent.postMessage(JSON.parse(JSON.stringify(message)), "*");
        return;
      }

      if (mjml.children && mjml.children[0]) {
        //@ts-expect-error
        this.mjml.children = [];
      }

      //@ts-expect-error
      if (mjml.tagName === "mjml") {
        //@ts-expect-error
        mjml.children[0] = mjml.children[0].children[0];
        mjml.tagName = "mj-global-style";
      }

      if (!mjml.children[0].attributes.containerWidth) {
        mjml.children[0].attributes.containerWidth = 600;
      }

      if (isMjml3(mjml)) {
        mjml = convertMjml3toMjml4(mjml);
      }

      // mjml = validateAndFixMjml(mjml);
      mjml = validateAndFixMjmlByZod(mjml);
      if (mjml.langs && mjml.langs.length > 0) {
        let primaryLang = mjml.langs.find((lang) => lang.primary);
        if (!primaryLang) {
          mjml.langs[0].primary = true;
          primaryLang = mjml.langs[0];
          this.emitLanguageCallback("onPrimaryLanguageChanged", [
            mjml.langs[0].key,
            langsToMutations(mjml.langs),
          ]);
        }
        this.langToShow = primaryLang.key;
        this.emitLanguageCallback("onLanguageSelected", [primaryLang.key]);
      }

      this.mjml = mjml;
      this.syncLanguageMergeTag();
      window.$event.emit("refreshSyncedSections");
    },

    goForward() {
      if (this.redoSnapshots.length > 0) {
        this.restoreRedoSnapshot();
      }
    },

    goBack() {
      if (this.snapshots.length > 0) {
        this.restoreSnapshot();
      }
    },

    restoreSnapshot() {
      this.redoSnapshots.unshift(JSON.parse(JSON.stringify(this.mjml)));
      this.redoShowLangSnapshots.unshift(this.langToShow);
      this.mjml = JSON.parse(JSON.stringify(this.snapshots[0]));
      this.langToShow = this.showLangSnapshots[0];
      this.emitLanguageCallback("onLanguageSelected", [this.langToShow]);
      this.snapshots.shift();
      this.showLangSnapshots.shift();
    },

    restoreRedoSnapshot() {
      this.snapshots.unshift(JSON.parse(JSON.stringify(this.mjml)));
      this.showLangSnapshots.unshift(this.langToShow);
      this.mjml = JSON.parse(JSON.stringify(this.redoSnapshots[0]));
      this.langToShow = this.redoShowLangSnapshots[0];
      this.emitLanguageCallback("onLanguageSelected", [this.langToShow]);
      this.redoSnapshots.shift();
      this.redoShowLangSnapshots.shift();
    },

    setBodyWidth(containerWidth: number) {
      this.updateContainerWidthOnSections(containerWidth);
      this.debounceUpdateContainerWidthOnBlocks(containerWidth);
    },

    createLanguage(lang: string, primary = false) {
      this.takeSnapshot();
      this.edittedWithoutSave = true;
      const transBlocks = this.getTranslatableBlocks;
      if (!this.mjml.langs) {
        this.mjml.langs = [];
        const trans: TopolTranslation = {};
        for (const block of transBlocks) {
          trans[block.uid] = getEmailPrimaryBlockTranslation(block);
        }
        this.mjml.langs.push({
          key: lang,
          primary: true,
          translations: trans,
        });
        this.langToShow = lang;
        this.emitLanguageCallback("onLanguageCreated", [
          lang,
          langsToMutations(this.mjml.langs),
        ]);
        this.emitLanguageCallback("onLanguageSelected", [this.langToShow]);
        return;
      }
      if (this.mjml.langs.find((l) => l.key === lang)) {
        return;
      }
      let translations: TopolTranslation = {};
      if (primary) {
        const currentPrimary = this.mjml.langs.find((l) => l.primary);
        if (currentPrimary) {
          currentPrimary.primary = false;
          translations = JSON.parse(
            JSON.stringify(currentPrimary.translations)
          );
        } else {
          //TODO: Co tady? Co KDyž něco prázndého? Celé procházet? asi jen content, href a src???
          translations = JSON.parse(
            JSON.stringify(this.mjml.langs[0].translations)
          );
        }
      } else {
        for (const block of transBlocks) {
          translations[block.uid] = {};
        }
      }
      const newLang = {
        key: lang,
        primary: primary,
        translations: translations,
      };
      this.mjml.langs.push(newLang);
      this.langToShow = lang;
      this.syncLanguageMergeTag();
      this.emitLanguageCallback("onLanguageCreated", [
        lang,
        langsToMutations(this.mjml.langs),
      ]);
      this.emitLanguageCallback("onLanguageSelected", [this.langToShow]);
    },

    changePrimaryLanguage(lang: string) {
      if (!this.mjml.langs || this.mjml.langs.length === 0) {
        return;
      }
      this.takeSnapshot();
      this.edittedWithoutSave = true;
      const currentPrimary = this.mjml.langs.find((l) => l.primary);
      let primaryTrans: TopolTranslation = {};
      if (currentPrimary) {
        currentPrimary.primary = false;
        primaryTrans = JSON.parse(JSON.stringify(currentPrimary.translations));
      }
      const newPrimary = this.mjml.langs.find((l) => l.key === lang);
      if (newPrimary) {
        newPrimary.primary = true;
        for (const block in primaryTrans) {
          if (!newPrimary.translations[block]) {
            newPrimary.translations[block] = primaryTrans[block];
          } else {
            if (
              primaryTrans[block].content &&
              !newPrimary.translations[block].content
            ) {
              newPrimary.translations[block].content =
                primaryTrans[block].content;
            }
            const primaryAttributes = primaryTrans[block].attributes;
            const newAttributes = newPrimary.translations[block].attributes;
            if (primaryAttributes && newAttributes) {
              type Attr = keyof typeof primaryAttributes;
              for (const attr in primaryAttributes) {
                if (
                  !newAttributes[attr as Attr] &&
                  primaryAttributes[attr as Attr]
                ) {
                  newAttributes[attr as Attr] = primaryAttributes[attr as Attr];
                }
              }
            }
            if (
              primaryAttributes &&
              !newPrimary.translations[block].attributes
            ) {
              newPrimary.translations[block].attributes = primaryAttributes;
            }
            const primaryChildren = primaryTrans[block].children;
            const newChildren = newPrimary.translations[block].children;
            if (primaryChildren && newChildren) {
              for (let i = 0; i < primaryChildren.length; i++) {
                if (!newChildren[i]) {
                  newChildren[i] = primaryChildren[i];
                  continue;
                }
                const primaryChildAttrs = primaryChildren[i].attributes;
                const newChildAttrs = newChildren[i].attributes;
                if (primaryChildAttrs && newChildAttrs) {
                  type Attr = keyof typeof primaryChildAttrs;
                  for (const attr in primaryChildAttrs) {
                    if (
                      !newChildAttrs[attr as Attr] &&
                      primaryChildAttrs[attr as Attr]
                    ) {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (newChildAttrs as any)[attr] =
                        primaryChildAttrs[attr as Attr];
                    }
                  }
                }
                if (primaryChildAttrs && !newChildren[i].attributes) {
                  newChildren[i].attributes = primaryChildAttrs;
                }
              }
            }
            if (primaryChildren && !newPrimary.translations[block].children) {
              newPrimary.translations[block].children = primaryChildren;
            }
            // Feed block: the new primary held a sparse per-slot override
            // array relative to the old primary. As the primary it must carry
            // a COMPLETE items array, so merge each override onto the old
            // primary item at the same slot (text overlay, full replacement,
            // or fall back to the old primary item for untranslated slots).
            const primaryItems = primaryTrans[block].items;
            if (Array.isArray(primaryItems)) {
              const newOverrides = newPrimary.translations[block].items;
              newPrimary.translations[block].items = primaryItems.map(
                (item: any, i: number) =>
                  mergeItemAtSlot(
                    item,
                    Array.isArray(newOverrides) ? newOverrides[i] : undefined
                  )
              );
            }
          }
        }
      } else {
        this.createLanguage(lang, true);
      }
      this.langToShow = lang;
      this.emitLanguageCallback("onPrimaryLanguageChanged", [
        lang,
        langsToMutations(this.mjml.langs),
      ]);
      this.emitLanguageCallback("onLanguageSelected", [this.langToShow]);
    },

    deleteLanguage(lang: string) {
      if (!this.mjml.langs || this.mjml.langs.length < 2) {
        return;
      }
      this.takeSnapshot();
      this.edittedWithoutSave = true;
      const langIndex = this.mjml.langs.findIndex((l) => l.key === lang);
      if (langIndex !== -1) {
        if (this.mjml.langs[langIndex].primary) {
          return;
        }
        const wasShowing = this.langToShow === lang;
        this.mjml.langs.splice(langIndex, 1);
        if (wasShowing) {
          this.langToShow =
            this.mjml.langs.find((l) => l.primary)?.key ||
            this.mjml.langs[0].key;
          this.emitLanguageCallback("onLanguageSelected", [this.langToShow]);
        }
        this.syncLanguageMergeTag();
        this.emitLanguageCallback("onLanguageDeleted", [
          lang,
          langsToMutations(this.mjml.langs),
        ]);
      }
    },

    syncLanguageMergeTag() {
      const generalStore = useGeneralStore();
      if (!generalStore.multilingual) return;

      const mergeTagsStore = useMergeTagsStore();
      const langCodes = this.mjml.langs?.map((l) => l.key) || [];

      mergeTagsStore.updateLanguageMergeTag(langCodes);
    },

    sendMutationsToParent() {
      this.emitLanguageCallback("onGetMutations", [
        langsToMutations(this.mjml.langs),
      ]);
    },
    emitLanguageCallback(action: string, args: unknown[]) {
      window.parent.postMessage(
        JSON.parse(
          JSON.stringify({
            type: "callback",
            action,
            data: { args },
          })
        ),
        "*"
      );
    },

    getDefaultContent(block: TopolBlock) {
      switch (block.tagName) {
        case "mj-button":
          return "Click me!";
        case "mj-text":
          return i18n.global.t("layout.default_text");
      }
    },

    updateContainerWidthOnSections(value: number) {
      this.mjml.attributes["containerWidth"] = value;
      this.mjml.children[0].attributes["containerWidth"] = value;

      for (const section of this.mjml.children[0].children) {
        section.attributes["containerWidth"] = value;
      }
    },

    updateContainerWidthOnBlocks(containerWidth: number) {
      for (const section of this.mjml.children[0].children) {
        //Get Columns
        for (const column of section.children) {
          const columnWidth = column.attributes["width"];
          const widthNum = parseInt(columnWidth);
          const width = containerWidth * (widthNum / 100);
          // Get blocks
          for (const block of column.children) {
            block.attributes["containerWidth"] = width;
          }
        }
      }
    },

    toggleSectionLock() {
      let newValue = false;
      const isLocked = this.getActiveSectionAttrString("locked");

      if (isLocked === false || isLocked === undefined) {
        newValue = true;
      }

      this.updateSectionAttribute({
        prop: "locked",
        value: newValue,
      });
    },

    selectSection(selectedSection: any): any {
      this.selectedSection = selectedSection;
      this.selectedBlock = null;
    },

    selectBlock(selectedBlock: any): any {
      this.selectedSection = null;
      this.selectedBlock = selectedBlock;
    },

    clearSelected() {
      this.selectedBlock = null;
      this.selectedSection = null;
    },

    addSection(data: any) {
      this.takeSnapshot();
      let newIndex = data.sectionIndex;
      if (
        !data.byClick &&
        newIndex !== 0 &&
        newIndex !== this.mjml.children[0].children.length
      ) {
        const { newIndex: correntNew } = this.getCorrectSectionIndexes({
          oldIndex: 0,
          newIndex: data.sectionIndex,
        });
        newIndex = correntNew;
      }
      const defaultsStore = useDefaultsStore();
      // @ts-expect-error
      const props: TopolSection = {
        uid: "",
        tagName: "mj-section",
        attributes: JSON.parse(
          JSON.stringify(defaultsStore.defaultSectionAttributes)
        ),
        children: data.columns,
      };

      // Apply global section settings (base attrs)
      const baseAttr = this.mjml.attributes["mj-section"];
      if (
        baseAttr &&
        !Array.isArray(baseAttr) &&
        typeof baseAttr === "object"
      ) {
        for (const [key, value] of Object.entries(baseAttr)) {
          if (value !== undefined && value !== null) {
            (props.attributes as Record<string, any>)[key] = value;
          }
        }
      }

      // Apply global column settings (base attrs)
      const colBaseAttr = this.mjml.attributes["mj-column"];
      if (
        colBaseAttr &&
        !Array.isArray(colBaseAttr) &&
        typeof colBaseAttr === "object"
      ) {
        for (const column of props.children) {
          for (const [key, value] of Object.entries(colBaseAttr)) {
            if (value !== undefined && value !== null) {
              (column.attributes as Record<string, any>)[key] = value;
            }
          }
        }
      }

      this.edittedWithoutSave = true;
      for (const property in this.defaultSectionProps) {
        // eslint-disable-next-line no-prototype-builtins
        if (this.defaultSectionProps.hasOwnProperty(property)) {
          const prop = property as keyof typeof this.defaultSectionProps;
          props[prop] = this.defaultSectionProps[prop] as never;
        }
      }
      props["uid"] = shortid.generate();
      const sectionIndex = data.inVariant ? data.sectionIndex : newIndex;

      const base = this.mjml.children[0];

      // copy previous or next section attributes
      if (
        base.children[sectionIndex] &&
        base.children[sectionIndex]["attributes"]
      ) {
        props["attributes"]["full-width"] =
          base.children[sectionIndex]["attributes"]["full-width"];
        // props["attributes"]["background-color"] =
        //   this.mjml.children[0].children[sectionIndex]["attributes"][
        //     "background-color"
        //   ];
      } else if (
        base.children[sectionIndex - 1] &&
        base.children[sectionIndex - 1]["attributes"]
      ) {
        props["attributes"]["full-width"] =
          base.children[sectionIndex - 1]["attributes"]["full-width"];
        // props["attributes"]["background-color"] =
        //   this.mjml.children[0].children[sectionIndex - 1]["attributes"][
        //     "background-color"
        //   ];
      }

      const variantOffset = this.getSectionIndexVariantOffset(
        sectionIndex,
        data.inVariant
      );

      base.children.splice(sectionIndex + variantOffset, 0, props);
    },

    addCopiedSection(data: any) {
      this.takeSnapshot();
      let newIndex = this.selectedSection?.sectionIndex || 0;
      if (data.direction === "bottom") {
        newIndex++;
      }
      this.edittedWithoutSave = true;
      const copiedSection = data.section;
      copiedSection["uid"] = shortid.generate();
      for (const column of copiedSection.children) {
        column["uid"] = shortid.generate();
        for (const block of column.children) {
          const oldUid = block.uid;
          block["uid"] = shortid.generate();
          this.duplicateBlockTranslations(block, oldUid);
        }
      }
      const sectionIndex = data.inVariant ? data.sectionIndex : newIndex;

      const variantOffset = this.getSectionIndexVariantOffset(
        sectionIndex,
        data.inVariant
      );

      this.mjml.children[0].children.splice(
        sectionIndex + variantOffset,
        0,
        copiedSection
      );
    },

    getSectionIndexVariantOffset(sectionIndex: number, inVariant?: boolean) {
      const sectionVariants = this.mjml.sectionVariants;
      let variantOffset = 0;

      if (sectionVariants && sectionVariants.length > 0 && !inVariant) {
        sectionVariants.forEach((variant) => {
          variant.sections.forEach((sectionId, index) => {
            if (
              this.mjml.children[0].children[sectionIndex] &&
              sectionId === this.mjml.children[0].children[sectionIndex].uid
            ) {
              variantOffset =
                (sectionId === variant.activeVariant
                  ? 0
                  : variant.sections.length) - index;
            }
          });
        });
      }

      return variantOffset;
    },

    duplicateSection(props: any) {
      this.takeSnapshot();
      this.edittedWithoutSave = true;
      const originalUid = this.mjml.children[0].children[props.sectionIndex]
        .uid as string;
      const copySection = JSON.parse(
        JSON.stringify(this.mjml.children[0].children[props.sectionIndex])
      );
      copySection.uid = shortid.generate();
      if (props.noSynced) {
        delete copySection.syncedId;
        delete copySection.syncedLang;
      }
      // Change uids of children
      const uidMap: Record<string, string> = {};
      for (const column of copySection.children) {
        column.uid = shortid.generate();
        // Blocks
        for (const block of column.children) {
          const oldUid = block.uid;
          block.uid = shortid.generate();
          uidMap[oldUid] = block.uid;
          this.duplicateBlockTranslations(block, oldUid);
        }
      }

      if (copySection.syncedId !== undefined) {
        const syncedTranslationsStore = useSyncedTranslationsStore();
        const entry =
          syncedTranslationsStore.getSectionTranslation(originalUid);
        if (entry) {
          syncedTranslationsStore.setSectionTranslation(copySection.uid, {
            fullTranslation: remapTranslationUids(
              entry.fullTranslation,
              uidMap
            ),
            pickedLang: entry.pickedLang,
          });
        }
      }

      const sectionVariants = this.mjml.sectionVariants;
      let variantOffset = 0;

      if (sectionVariants && sectionVariants.length > 0 && !props.inVariant) {
        sectionVariants.forEach((variant) => {
          variant.sections.forEach((sectionId, index) => {
            if (
              sectionId ===
              this.mjml.children[0].children[props.sectionIndex].uid
            ) {
              variantOffset = variant.sections.length - 1 - index;
            }
          });
        });
      }
      this.mjml.children[0].children.splice(
        props.sectionIndex + variantOffset + 1,
        0,
        copySection
      );
    },

    // Detach a synced section from its synced source, turning it into a normal
    // editable section. The section keeps its uid, so its translations (stored
    // in the synced translations store by uid) are preserved.
    unlinkSyncedSection(props: { sectionIndex: number }) {
      const section = this.getSelectedSectionByIndex(props.sectionIndex);
      if (!section) {
        return;
      }
      this.takeSnapshot();
      this.edittedWithoutSave = true;
      delete section.syncedId;
      delete section.syncedLang;
    },

    deleteSection(props: any): any {
      if (!props.withoutVariantCheck) this.takeSnapshot();
      if (
        this.mjml.children[0].children.length > 1 &&
        this.mjml.children[0].children[props.sectionIndex]
      ) {
        if (this.selectedBlock || this.mjml.langs) {
          for (const column of this.mjml.children[0].children[
            props.sectionIndex
          ].children) {
            for (const block of column.children) {
              if (
                this.selectedBlock &&
                block.uid ==
                  this.mjml.children[0].children[
                    this.selectedBlock.sectionIndex
                  ].children[this.selectedBlock.columnIndex].children[
                    this.selectedBlock.blockIndex
                  ].uid
              ) {
                this.selectedBlock = null;
              }
              this.deleteBlockTranslation(block);
            }
          }
        }

        if (!props.withoutVariantCheck) {
          const sectionVariants = this.mjml.sectionVariants;

          if (sectionVariants && sectionVariants.length > 0) {
            sectionVariants.forEach((variant) => {
              variant.sections.forEach((sectionId) => {
                if (
                  sectionId ===
                  this.mjml.children[0].children[props.sectionIndex].uid
                ) {
                  const sectionVariantsStore = useSectionVariantsStore();
                  sectionVariantsStore.deleteVariant(sectionId);
                }
              });
            });
          }
        }

        if (!props.withoutVariantCheck) this.edittedWithoutSave = true;
        // NOTE: the section's syncedTranslationsStore entry is intentionally
        // kept — undo can bring the section back with the same uids, and the
        // entry must still match then. It is runtime-only and tiny.
        this.mjml.children[0].children.splice(props.sectionIndex, 1);
        if (!props.withoutVariantCheck) this.selectedSection = null;
      }
    },

    addBlock(props: any): string | null {
      let blockUid: string | null = null;
      this.takeSnapshot();
      if (!this.mjml || !this.mjml.children || !this.mjml.children[0]) {
        return blockUid;
      }
      const base = this.mjml.children[0];

      if (
        !base ||
        !base.children ||
        !props ||
        props.sectionIndex === undefined ||
        !base.children[props.sectionIndex]
      ) {
        return blockUid;
      }
      const section = base.children[props.sectionIndex];

      if (
        !section ||
        !section.children ||
        props.columnIndex === undefined ||
        !section.children[props.columnIndex]
      ) {
        return blockUid;
      }
      const column = section.children[props.columnIndex];
      this.edittedWithoutSave = true;

      if (!column.children) column.children = [];

      let newIndex = props.blockIndex;

      if (
        !props.byClick &&
        props.blockIndex !== 0 &&
        props.blockIndex !== column.children.length
      ) {
        const { newIndex: crorrectIndex } = this.getCorrectBlockIndexes({
          oldIndex: 0,
          newIndex: props.blockIndex,
          blocks: column.children,
        });
        newIndex = crorrectIndex;
      }
      const block = resolveBlock(props.type);

      if (!block) {
        return blockUid;
      }

      const objectData = block.getData(
        this.$state,
        props,
        props.customBlockData
      );
      if (!objectData) {
        return blockUid;
      }

      if (Array.isArray(objectData)) {
        let blockIndex = newIndex;
        for (const block of objectData) {
          if (!block) {
            continue;
          }
          blockUid = shortid.generate();
          block.uid = blockUid;
          column.children.splice(blockIndex, 0, block);
          blockIndex++;
        }
        return blockUid;
      }
      // We basically know what block is about ot be added, so we can safely assume that it has uid property
      const uid = shortid.generate();
      objectData.uid = uid;
      blockUid = uid;

      //temporary store container width
      const containerWidth = objectData.attributes.containerWidth;

      if (
        props.customBlockData == "product" ||
        props.type == "dynamicproduct"
      ) {
        let lastProduct = null;

        // Sections
        for (const productSection of this.mjml.children[0].children) {
          // Columns
          if (productSection.children) {
            for (const productColumn of productSection.children) {
              // Blocks
              if (productColumn.children) {
                for (const productBlock of productColumn.children) {
                  if (
                    productBlock &&
                    (productBlock.tagName == "mj-product" ||
                      // @ts-expect-error
                      productBlock.tagName == "mj-dynamic-product")
                  ) {
                    lastProduct = productBlock;
                  }
                }
              }
            }
          }
        }

        // TODO: this lad product is so silly condition that it should be refactored
        if (lastProduct) {
          objectData.attributes.prodstructure = false;
          //set container width to calculated width
          objectData.attributes["containerWidth"] = containerWidth;
        }
      }
      column.children.splice(newIndex, 0, objectData);
      this.addBlockTranslations(objectData);

      return blockUid;
    },

    addBlockTranslations(block: TopolBlock) {
      block = JSON.parse(JSON.stringify(block));
      if (
        this.mjml.langs &&
        this.mjml.langs.length > 0 &&
        emailTranslatableBlockTags.includes(
          block.tagName as ITranslatableBlockTag
        )
      ) {
        for (const lang of this.mjml.langs) {
          if (lang.primary || lang.key === this.langToShow) {
            lang.translations[block.uid] =
              getEmailPrimaryBlockTranslation(block);
          } else {
            lang.translations[block.uid] = {};
          }
        }
      }
    },

    duplicateBlockTranslations(block: TopolBlock, oldUid: string) {
      block = JSON.parse(JSON.stringify(block));
      if (
        this.mjml.langs &&
        this.mjml.langs.length > 0 &&
        emailTranslatableBlockTags.includes(
          block.tagName as ITranslatableBlockTag
        )
      ) {
        for (const lang of this.mjml.langs) {
          if (lang.translations[oldUid]) {
            lang.translations[block.uid] = JSON.parse(
              JSON.stringify(lang.translations[oldUid])
            );
          } else {
            if (lang.primary || lang.key === this.langToShow) {
              lang.translations[block.uid] =
                getEmailPrimaryBlockTranslation(block);
            } else {
              lang.translations[block.uid] = {};
            }
          }
        }
      }
    },

    addSpecialBlockTranslations(props: {
      block: TopolBlock;
      previousUid: string;
      translations?: TopolSpecialBlockTranslation;
      primaryTranslation?: TopolSpecialBlockTranslation[string];
    }) {
      addEmailSpecialBlockTranslation(this.mjml, props);
    },

    addCopiedBlock(props: any): string | null {
      let blockUid: string | null = null;
      this.takeSnapshot();
      if (!this.mjml || !this.mjml.children || !this.mjml.children[0]) {
        return blockUid;
      }
      const base = this.mjml.children[0];
      const sectionIndex = this.selectedBlock?.sectionIndex;

      if (
        !base ||
        !base.children ||
        !props ||
        sectionIndex === undefined ||
        !base.children[sectionIndex]
      ) {
        return blockUid;
      }
      const section = base.children[sectionIndex];
      const columnIndex = this.selectedBlock?.columnIndex;

      if (
        !section ||
        !section.children ||
        columnIndex === undefined ||
        !section.children[columnIndex]
      ) {
        return blockUid;
      }
      const column = section.children[columnIndex];
      this.edittedWithoutSave = true;

      if (!column.children) column.children = [];
      let newIndex = this.selectedBlock?.blockIndex || 0;

      if (props.direction === "bottom") {
        newIndex++;
      }

      const block = props.block;
      const oldUid = block.uid;
      const uid = shortid.generate();
      block.uid = uid;
      blockUid = uid;

      column.children.splice(newIndex, 0, block);
      this.duplicateBlockTranslations(block, oldUid);

      return blockUid;
    },

    addBlockChild(props: any): any {
      this.takeSnapshot();
      if (!this.mjml || !this.mjml.children) {
        return;
      }

      const base = this.mjml.children[0];

      if (
        !base ||
        !base.children ||
        !props ||
        props.sectionIndex == undefined
      ) {
        return;
      }
      const section = base.children[props.sectionIndex];

      if (!section || !section.children || props.columnIndex == undefined) {
        return;
      }
      const column = section.children[props.columnIndex];

      if (
        !column ||
        !column.children ||
        props.blockIndex == undefined ||
        !column.children[props.blockIndex]
      ) {
        return;
      }
      const block = column.children[props.blockIndex];
      if (
        !(
          block.tagName === "mj-carousel" ||
          block.tagName === "mj-social" ||
          block.tagName === "mj-rating"
        )
      ) {
        return;
      }
      this.edittedWithoutSave = true;

      block.children.splice(props.childIndex, 0, props.value);
      // eslint-disable-next-line no-self-assign
      this.mjml = this.mjml;
      if (this.mjml.langs && this.mjml.langs.length > 0) {
        for (const lang of this.mjml.langs) {
          if (!lang.translations[block.uid]) {
            lang.translations[block.uid] = {};
          }
          if (!lang.translations[block.uid].children) {
            lang.translations[block.uid].children = [];
          }
          const children = lang.translations[block.uid].children;
          if (!children) {
            continue;
          }
          if (lang.primary || lang.key === this.langToShow) {
            children.splice(
              props.childIndex,
              0,
              getEmailPrimaryBlockTranslation(props.value)
            );
          } else {
            children.splice(props.childIndex, 0, {});
          }
        }
      }
    },

    duplicateBlock(props: any): any {
      this.takeSnapshot();

      if (this.mjml && this.mjml.children && this.mjml.children[0]) {
        const base = this.mjml.children[0];
        if (
          base &&
          base.children &&
          props &&
          props.sectionIndex != undefined &&
          base.children[props.sectionIndex]
        ) {
          const section = base.children[props.sectionIndex];
          if (
            section &&
            section.children &&
            props.columnIndex != undefined &&
            section.children[props.columnIndex]
          ) {
            const column = section.children[props.columnIndex];
            if (
              column &&
              column.children &&
              props.blockIndex != undefined &&
              column.children[props.blockIndex]
            ) {
              this.edittedWithoutSave = true;
              const copyBlock = JSON.parse(
                JSON.stringify(column.children[props.blockIndex])
              );
              const oldUid = copyBlock.uid;
              copyBlock.uid = shortid.generate();
              column.children.splice(props.blockIndex, 0, copyBlock);
              this.duplicateBlockTranslations(copyBlock, oldUid);
            }
          }
        }
      }
    },

    deleteBlock(props: any): any {
      this.takeSnapshot();
      if (this.mjml && this.mjml.children && this.mjml.children[0]) {
        const base = this.mjml.children[0];
        if (
          base &&
          base.children &&
          props &&
          props.sectionIndex != undefined &&
          base.children[props.sectionIndex]
        ) {
          const section = base.children[props.sectionIndex];
          if (
            section &&
            section.children &&
            props.columnIndex != undefined &&
            section.children[props.columnIndex]
          ) {
            const column = section.children[props.columnIndex];
            if (
              column &&
              column.children &&
              props.blockIndex != undefined &&
              column.children[props.blockIndex]
            ) {
              this.edittedWithoutSave = true;
              this.deleteBlockTranslation(column.children[props.blockIndex]);
              column.children.splice(props.blockIndex, 1);
              this.selectedSection = null;
              this.selectedBlock = null;
            }
          }
        }
      }
    },

    deleteBlockTranslation(block: any): any {
      if (
        this.mjml.langs &&
        this.mjml.langs.length > 0 &&
        emailTranslatableBlockTags.includes(
          block.tagName as ITranslatableBlockTag
        )
      ) {
        for (const lang of this.mjml.langs) {
          if (lang.translations[block.uid]) {
            delete lang.translations[block.uid];
          }
        }
      }
    },

    deleteBlockChild(props) {
      if (!this.mjml || !this.mjml.children) {
        return;
      }
      const base = this.mjml.children[0];
      if (
        !base ||
        !base.children ||
        !props ||
        props.sectionIndex == undefined
      ) {
        return;
      }
      const section = base.children[props.sectionIndex];
      if (!section || !section.children || props.columnIndex == undefined) {
        return;
      }
      const column = section.children[props.columnIndex];
      if (!column || !column.children || props.blockIndex == undefined) {
        return;
      }
      const block = column.children[props.blockIndex];
      if (
        !block ||
        !(
          block.tagName === "mj-carousel" ||
          block.tagName === "mj-social" ||
          block.tagName === "mj-rating"
        ) ||
        !block.children ||
        props.childIndex == undefined ||
        !block.children[props.childIndex]
      ) {
        return;
      }
      this.takeSnapshot();
      this.edittedWithoutSave = true;
      block.children.splice(props.childIndex, 1);
      if (this.mjml.langs && this.mjml.langs.length > 0) {
        for (const lang of this.mjml.langs) {
          if (
            lang.translations[block.uid] &&
            lang.translations[block.uid].children
          ) {
            const children = lang.translations[block.uid].children;
            if (!children) {
              continue;
            }
            children.splice(props.childIndex, 1);
          }
        }
      }
    },

    setBlockChildren(props: any): any {
      if (this.mjml && this.mjml.children && this.mjml.children[0]) {
        const base = this.mjml.children[0];
        if (
          base &&
          base.children &&
          props &&
          props.sectionIndex != undefined &&
          base.children[props.sectionIndex]
        ) {
          const section = base.children[props.sectionIndex];
          if (
            section &&
            section.children &&
            props.columnIndex != undefined &&
            section.children[props.columnIndex]
          ) {
            const column = section.children[props.columnIndex];
            if (
              column &&
              column.children &&
              props.blockIndex != undefined &&
              column.children[props.blockIndex]
            ) {
              const block = column.children[props.blockIndex];
              if (
                block.tagName === "mj-carousel" ||
                block.tagName === "mj-social" ||
                block.tagName === "mj-rating"
              ) {
                this.takeSnapshot();
                this.edittedWithoutSave = true;

                block.children = props.value;
                // eslint-disable-next-line no-self-assign
                this.mjml = this.mjml;
              }
            }
          }
        }
      }
    },

    updateBlockContentTranslation(props: any, block: TopolBlock): any {
      if (
        this.mjml.langs &&
        this.mjml.langs.length > 0 &&
        emailTranslatableBlockTags.includes(
          block.tagName as ITranslatableBlockTag
        ) &&
        props.prop === "content"
      ) {
        let translation = this.mjml.langs.find(
          (lang) => lang.key === this.langToShow
        );
        if (!translation) {
          const defaultLang = this.mjml.langs.find((lang) => lang.primary);
          if (!defaultLang) {
            this.mjml.langs[0].primary = true;
            translation = this.mjml.langs[0];
            this.emitLanguageCallback("onPrimaryLanguageChanged", [
              this.mjml.langs[0].key,
              langsToMutations(this.mjml.langs),
            ]);
          } else {
            translation = defaultLang;
          }
          this.langToShow = translation.key;
          this.emitLanguageCallback("onLanguageSelected", [this.langToShow]);
        }
        if (translation.translations[block.uid]) {
          translation.translations[block.uid].content = props.value as string;
        } else {
          if (translation.primary) {
            translation.translations[block.uid] =
              getEmailPrimaryBlockTranslation(block);
          } else {
            translation.translations[block.uid] = {};
          }
          translation.translations[block.uid].content = props.value as string;
        }
        return true;
      }
      return false;
    },

    updateBlockProp(props: any): any {
      const selectBlock = this.getSelectedBlock;
      if (!selectBlock) {
        return;
      }
      if (!props.skipSnapshot) {
        if (props.instantSnapshot) {
          this.takeSnapshot();
        } else {
          this.throttledTakeSnapshot();
        }
      }
      this.edittedWithoutSave = true;
      const wasTranslated = this.updateBlockContentTranslation(
        props,
        selectBlock
      );
      if (wasTranslated) {
        return;
      }
      const wasPropTranslated = this.updateBlockPropTranslation(
        props,
        selectBlock
      );
      if (wasPropTranslated) {
        // Mirror current lang value onto the block so canvas reactivity
        // shows the latest text in the active language.
        selectBlock[props.prop as keyof typeof selectBlock] = props.value;
        return;
      }
      selectBlock[props.prop as keyof typeof selectBlock] = props.value;
    },

    updateSpecificBlockProp(props: any): any {
      if (props.instantSnapshot) {
        this.takeSnapshot();
      } else {
        this.throttledTakeSnapshot();
      }

      if (this.mjml && this.mjml.children && this.mjml.children[0]) {
        const base = this.mjml.children[0];
        if (
          base &&
          base.children &&
          props &&
          props.sectionIndex != undefined &&
          base.children[props.sectionIndex]
        ) {
          const section = base.children[props.sectionIndex];
          if (
            section &&
            section.children &&
            props.columnIndex != undefined &&
            section.children[props.columnIndex]
          ) {
            const column = section.children[props.columnIndex];
            if (
              column &&
              column.children &&
              props.blockIndex != undefined &&
              column.children[props.blockIndex]
            ) {
              this.edittedWithoutSave = true;
              const selectBlock = column.children[props.blockIndex];
              if (props.uid && selectBlock.uid !== props.uid) {
                return;
              }

              if (!props.product) {
                const wasTranslated = this.updateBlockContentTranslation(
                  props,
                  selectBlock
                );
                if (wasTranslated) {
                  return;
                }
                selectBlock[props.prop as keyof typeof selectBlock] =
                  props.value;
              } else {
                selectBlock.attributes[props.prop] = props.value;
              }
            }
          }
        }
      }
    },

    /**
     * Mirrors updateBlockAttributeTranslation but for top-level translatable
     * props (e.g. mj-rating's questionText/captionTextLeft/captionTextRight).
     * Writes to translation.translations[uid][prop] when in multilang mode.
     */
    updateBlockPropTranslation(props: any, block: TopolBlock): boolean {
      if (props.prop === "items" && block.tagName === "mj-feed-block") {
        return false;
      }
      if (
        this.mjml.langs &&
        this.mjml.langs.length > 0 &&
        isTranslatableProp(props.prop, block.tagName)
      ) {
        let translation = this.mjml.langs.find(
          (lang) => lang.key === this.langToShow
        );
        if (!translation) {
          const defaultLang = this.mjml.langs.find((lang) => lang.primary);
          if (!defaultLang) {
            this.mjml.langs[0].primary = true;
            translation = this.mjml.langs[0];
          } else {
            translation = defaultLang;
          }
          this.langToShow = translation.key;
        }
        let blockTrans = translation.translations[block.uid];
        if (!blockTrans) {
          if (translation.primary) {
            translation.translations[block.uid] =
              getEmailPrimaryBlockTranslation(block);
          } else {
            translation.translations[block.uid] = {};
          }
          blockTrans = translation.translations[block.uid];
        }
        if (
          !translation.primary &&
          (props.value === "" || props.value == null)
        ) {
          delete (blockTrans as any)[props.prop];
        } else {
          (blockTrans as any)[props.prop] = props.value;
        }
        return true;
      }
      return false;
    },

    updateBlockAttributeTranslation(props: any, block: TopolBlock): boolean {
      if (
        this.mjml.langs &&
        this.mjml.langs.length > 0 &&
        isTranslatableAttribute(props.prop, block.tagName)
      ) {
        let translation = this.mjml.langs.find(
          (lang) => lang.key === this.langToShow
        );
        if (!translation) {
          const defaultLang = this.mjml.langs.find((lang) => lang.primary);
          if (!defaultLang) {
            this.mjml.langs[0].primary = true;
            translation = this.mjml.langs[0];
            this.emitLanguageCallback("onPrimaryLanguageChanged", [
              this.mjml.langs[0].key,
              langsToMutations(this.mjml.langs),
            ]);
          } else {
            translation = defaultLang;
          }
          this.langToShow = translation.key;
          this.emitLanguageCallback("onLanguageSelected", [this.langToShow]);
        }
        let blockTrans = translation.translations[block.uid];
        if (blockTrans) {
          if (!blockTrans.attributes) {
            blockTrans.attributes = {};
          }
          blockTrans.attributes[props.prop as TranslationAttributes] =
            props.value as string;
        } else {
          if (translation.primary) {
            translation.translations[block.uid] =
              getEmailPrimaryBlockTranslation(block);
            blockTrans = translation.translations[block.uid];
          } else {
            translation.translations[block.uid] = {};
            blockTrans = translation.translations[block.uid];
          }
          if (!blockTrans.attributes) {
            blockTrans.attributes = {};
          }
          blockTrans.attributes[props.prop as TranslationAttributes] =
            props.value as string;
        }
        return true;
      }
      return false;
    },

    updateBlockAttribute(props: any): any {
      const selectBlock = this.getSelectedBlock;
      if (!selectBlock) {
        return;
      }

      if (!props.skipSnapshot) {
        this.throttledTakeSnapshot();
      }

      this.edittedWithoutSave = true;
      if (
        props.product &&
        (selectBlock.tagName === "mj-product" ||
          selectBlock.tagName === "mj-article" ||
          selectBlock.tagName === "mj-feed-block") &&
        selectBlock.blockName &&
        (this.mjml.feedBlocksApplyToAll[selectBlock.blockName] ||
          props.prodstructure)
      ) {
        // Sections
        this.mjml.children[0].children.forEach((section) => {
          // Columns
          if (section.children) {
            section.children.forEach((column) => {
              // Blocks
              if (column.children) {
                column.children.forEach((block) => {
                  if (
                    (block.tagName === "mj-product" ||
                      block.tagName === "mj-article" ||
                      block.tagName === "mj-feed-block") &&
                    block.blockName === selectBlock.blockName
                  ) {
                    //block.attributes[props.prop] = props.value
                    if (
                      !props.prodstructure ||
                      (props.prodstructure && block.attributes["prodstructure"])
                    ) {
                      block.attributes[props.prop] = props.value;
                    }
                  }
                });
              } else {
                column.children = [];
              }
            });
          }
        });
      } else {
        const wasTranslated = this.updateBlockAttributeTranslation(
          props,
          selectBlock
        );
        if (wasTranslated) {
          return;
        }
        selectBlock.attributes[props.prop] = props.value;
      }
    },

    updateBlockItemAttribute(props: any): any {
      const selectBlock = this.getSelectedBlock as any;
      if (!selectBlock) {
        return;
      }

      if (!props.skipSnapshot) {
        this.throttledTakeSnapshot();
      }

      this.edittedWithoutSave = true;

      if (selectBlock.tagName === "mj-feed-block") {
        this.updateFeedItemField({
          index: props.itemIndex,
          field: props.prop,
          value: props.value,
        });
        return;
      }

      if (!("items" in selectBlock) || !selectBlock.items[props.itemIndex]) {
        return;
      }
      const item = selectBlock.items[props.itemIndex];

      item[props.prop] = props.value;
    },

    updateAllSiblingFeedBlockItemsAttribute(props: {
      blockName: string;
      prop: string;
      value: any;
      prodstructure?: string | boolean;
      skipSnapshot?: boolean;
    }): void {
      if (!props.skipSnapshot) {
        this.throttledTakeSnapshot();
      }
      this.edittedWithoutSave = true;

      const onPrimary = this.isOnPrimaryLang();
      const activeLang =
        !onPrimary && this.mjml.langs
          ? this.mjml.langs.find((l) => l.key === this.langToShow)
          : null;

      this.mjml.children[0].children.forEach((section: any) => {
        section.children?.forEach((column: any) => {
          column.children?.forEach((block: any) => {
            if (
              !(
                (block.tagName === "mj-product" ||
                  block.tagName === "mj-article" ||
                  block.tagName === "mj-feed-block") &&
                block.blockName === props.blockName &&
                (!props.prodstructure || block.attributes?.["prodstructure"])
              )
            ) {
              return;
            }

            if (block.tagName !== "mj-feed-block") {
              if (!block.items?.length) return;
              block.items.forEach((item: any) => {
                item[props.prop] = props.value;
              });
              return;
            }

            // mj-feed-block: items source lives in translations[primary]
            // (or block.items as legacy fallback). Skip if neither has data.
            const primaryItems = this._resolvePrimaryFeedItems(block);
            if (!primaryItems.length) return;
            if (onPrimary) {
              for (const item of primaryItems) {
                item[props.prop] = props.value;
              }
              return;
            }

            if (!activeLang) return;
            if (!activeLang.translations[block.uid])
              activeLang.translations[block.uid] = {};
            const blockTrans = activeLang.translations[block.uid] as any;
            let currentItems: (any | null)[] | undefined = Array.isArray(
              blockTrans.items
            )
              ? blockTrans.items
              : undefined;
            primaryItems.forEach((primaryItem: any, i: number) => {
              currentItems = this._upsertFeedItemFieldOverride(
                currentItems,
                i,
                primaryItem.id,
                props.prop,
                props.value
              );
            });
            blockTrans.items = currentItems;
          });
        });
      });
    },

    updateSpecificBlockAttribute(props: any): any {
      this.throttledTakeSnapshot();

      if (this.mjml && this.mjml.children && this.mjml.children[0]) {
        const base = this.mjml.children[0];
        if (
          base &&
          base.children &&
          props &&
          props.sectionIndex != undefined &&
          base.children[props.sectionIndex]
        ) {
          const section = base.children[props.sectionIndex];
          if (
            section &&
            section.children &&
            props.columnIndex != undefined &&
            section.children[props.columnIndex]
          ) {
            const column = section.children[props.columnIndex];
            if (
              column &&
              column.children &&
              props.blockIndex != undefined &&
              column.children[props.blockIndex]
            ) {
              this.edittedWithoutSave = true;
              const selectBlock = column.children[props.blockIndex];
              const wasTranslated = this.updateBlockAttributeTranslation(
                props,
                selectBlock
              );
              if (wasTranslated) {
                return;
              }
              selectBlock.attributes[props.prop] = props.value;
            }
          }
        }
      }
    },

    updateSpecificBlockItemAttribute(props: any): any {
      this.throttledTakeSnapshot();

      if (!this.mjml || !this.mjml.children || !this.mjml.children[0]) {
        return;
      }
      const base = this.mjml.children[0];

      if (
        !base ||
        !base.children ||
        !props ||
        props.sectionIndex == undefined ||
        !base.children[props.sectionIndex]
      ) {
        return;
      }

      const section = base.children[props.sectionIndex];

      if (
        !section ||
        !section.children ||
        props.columnIndex == undefined ||
        !section.children[props.columnIndex]
      ) {
        return;
      }

      const column = section.children[props.columnIndex];

      if (
        !column ||
        !column.children ||
        props.blockIndex == undefined ||
        !column.children[props.blockIndex]
      ) {
        return;
      }
      this.edittedWithoutSave = true;
      const selectBlock = column.children[props.blockIndex] as any;
      if (selectBlock.tagName === "mj-feed-block") {
        this._writeFeedItemField(
          selectBlock,
          props.itemIndex,
          props.prop,
          props.value
        );
        return;
      }
      if (!("items" in selectBlock) || !selectBlock.items[props.itemIndex]) {
        return;
      }
      const item = selectBlock.items[props.itemIndex];
      item[props.prop] = props.value;
    },

    updateBlockChildAttributeTranslation(
      props: any,
      block: TopolBlock
    ): boolean {
      if (
        this.mjml.langs &&
        this.mjml.langs.length > 0 &&
        isTranslatableChildAttribute(props.prop, block.tagName)
      ) {
        let translation = this.mjml.langs.find(
          (lang) => lang.key === this.langToShow
        );
        if (!translation) {
          const defaultLang = this.mjml.langs.find((lang) => lang.primary);
          if (!defaultLang) {
            this.mjml.langs[0].primary = true;
            translation = this.mjml.langs[0];
            this.emitLanguageCallback("onPrimaryLanguageChanged", [
              this.mjml.langs[0].key,
              langsToMutations(this.mjml.langs),
            ]);
          } else {
            translation = defaultLang;
          }
          this.langToShow = translation.key;
          this.emitLanguageCallback("onLanguageSelected", [this.langToShow]);
        }
        let blockTrans = translation.translations[block.uid];
        if (blockTrans) {
          if (!blockTrans.children) {
            blockTrans.children = [];
          }
          if (!blockTrans.children[props.childIndex]) {
            blockTrans.children[props.childIndex] = {};
          }
          const child = blockTrans.children[props.childIndex];
          if (!child.attributes) {
            child.attributes = {};
          }
          child.attributes[props.prop as TranslationChildAttributes] =
            props.value as string;
        } else {
          if (translation.primary) {
            translation.translations[block.uid] =
              getEmailPrimaryBlockTranslation(block);
            blockTrans = translation.translations[block.uid];
          } else {
            translation.translations[block.uid] = {};
            blockTrans = translation.translations[block.uid];
          }
          if (!blockTrans.children) {
            blockTrans.children = [];
          }
          if (!blockTrans.children[props.childIndex]) {
            blockTrans.children[props.childIndex] = {};
          }
          const child = blockTrans.children[props.childIndex];
          if (!child.attributes) {
            child.attributes = {};
          }
          child.attributes[props.prop as TranslationChildAttributes] =
            props.value as string;
        }
        return true;
      }
      return false;
    },

    updateBlockChildrenAttribute(props: {
      prop: string;
      value: any;
      childIndex: number;
    }): any {
      const block = this.getSelectedBlock;
      if (
        !block ||
        !(
          block.tagName === "mj-carousel" ||
          block.tagName === "mj-social" ||
          block.tagName === "mj-rating"
        ) ||
        !block.children ||
        !block.children[props.childIndex]
      ) {
        return;
      }
      this.throttledTakeSnapshot();
      const wasTranslated = this.updateBlockChildAttributeTranslation(
        props,
        block
      );
      if (wasTranslated) {
        // Mirror current lang's value onto block.children so canvas + form
        // display reflect the active language immediately.
        if (block.children[props.childIndex]?.attributes) {
          block.children[props.childIndex].attributes[
            props.prop as keyof (typeof block.children)[0]["attributes"]
          ] = props.value;
        }
        return;
      }
      if (block.children[props.childIndex]?.attributes) {
        this.edittedWithoutSave = true;
        block.children[props.childIndex].attributes[
          props.prop as keyof (typeof block.children)[0]["attributes"]
        ] = props.value;
      }
    },

    updateBlockChildrenAttributeForAllMutations(props: {
      prop: string;
      value: any;
      childIndex: number;
    }): any {
      const block = this.getSelectedBlock;
      if (
        !block ||
        !(
          block.tagName === "mj-carousel" ||
          block.tagName === "mj-social" ||
          block.tagName === "mj-rating"
        ) ||
        !block.children ||
        !block.children[props.childIndex]
      ) {
        return;
      }

      const mjml = this.mjml as TopolTemplate;
      if (
        !isTranslatableChildAttribute(props.prop, block.tagName) ||
        !mjml.langs?.length
      ) {
        return;
      }

      mjml.langs.forEach((lang) => {
        // Initialize structure only for primary language
        if (lang.primary) {
          if (!lang.translations[block.uid]) {
            lang.translations[block.uid] = { children: [] };
          }

          const translation = lang.translations[block.uid];
          if (!translation.children) {
            translation.children = [];
          }

          if (!translation.children[props.childIndex]) {
            translation.children[props.childIndex] = { attributes: {} };
          }

          if (!translation.children[props.childIndex].attributes) {
            translation.children[props.childIndex].attributes = {};
          }
        }

        // If structure doesn't exist and it's not primary language, skip
        const childAttr =
          lang.translations[block.uid]?.children?.[props.childIndex]
            ?.attributes;
        if (!childAttr) {
          return;
        }

        // Set value: original for primary, empty string for others
        childAttr[props.prop as TranslationChildAttributes] = lang.primary
          ? (props.value as string)
          : "";
      });
    },

    updateLoopBlockChildAttribute(props: {
      columnIndex: number;
      blockIndex: number;
      prop: string;
      value: any;
    }): any {
      const selectedBlock = this.getActiveBlock;
      if (
        !selectedBlock ||
        selectedBlock.tagName !== "mj-loop" ||
        selectedBlock.children[props.columnIndex] === undefined
      ) {
        return;
      }
      const column = selectedBlock.children[props.columnIndex];

      if (!column.children[props.blockIndex]) {
        return;
      }
      const blockAttr = column.children[props.blockIndex].attributes;
      this.throttledTakeSnapshot();
      this.edittedWithoutSave = true;
      blockAttr[props.prop as keyof typeof blockAttr] = props.value as never;
    },

    updateLoopBlockChildProp(props: {
      columnIndex: number;
      blockIndex: number;
      prop: string;
      value: string;
    }): any {
      const selectedBlock = this.getActiveBlock;
      if (
        !selectedBlock ||
        selectedBlock.tagName !== "mj-loop" ||
        selectedBlock.children[props.columnIndex] === undefined
      ) {
        return;
      }
      const column = selectedBlock.children[props.columnIndex];
      if (!column.children[props.blockIndex]) {
        return;
      }
      const block = column.children[props.blockIndex];
      if (block.tagName === "mj-image") {
        return;
      }
      this.throttledTakeSnapshot();
      this.edittedWithoutSave = true;
      block[props.prop as keyof typeof block] = props.value as never;
    },

    updateSectionAttribute(props: any): any {
      const selectedSection = this.getSelectedSectionByIndex();

      if (selectedSection) {
        this.throttledTakeSnapshot();
        this.edittedWithoutSave = true;
        selectedSection.attributes[props.prop] = props.value;
      }
    },

    updateSelectedSectionProp(props: { prop: string; value: any }): any {
      const selectedSection = this.getSelectedSectionByIndex();

      if (selectedSection) {
        this.throttledTakeSnapshot();
        this.edittedWithoutSave = true;
        selectedSection[props.prop] = props.value;
      }
    },

    updateSpecificColumnAttribute(props: {
      columnIndex: number;
      prop: keyof TopolColumn["attributes"];
      value: TopolColumn["attributes"][keyof TopolColumn["attributes"]];
    }): any {
      const section = this.getSelectedSectionByIndex();
      if (!section) {
        return;
      }
      if (
        section &&
        section.children &&
        props &&
        props.columnIndex != undefined &&
        section.children[props.columnIndex]
      ) {
        this.throttledTakeSnapshot();
        this.edittedWithoutSave = true;
        section.children[props.columnIndex].attributes[props.prop] =
          props.value;
      }
    },

    updateBlockContentByAI(props: any): any {
      this.edittedWithoutSave = true;
      const base = this.getBase;
      if (!base) return;

      for (const section of base.children) {
        for (const column of section.children) {
          const thisBlock = column.children.find(
            (item: any) => item.uid === props.uid
          );
          if (thisBlock) {
            this.takeSnapshot();
            thisBlock.content = props.newContent;
          }
        }
      }
    },

    updateSpecificBlockColumnAttribute(props: {
      columnIndex: number;
      prop: keyof TopolLoopColumn["attributes"];
      value: TopolLoopColumn["attributes"][keyof TopolLoopColumn["attributes"]];
    }): any {
      const block = this.getSelectedBlock;
      if (
        !block ||
        !this.tagsWithColumnChildren.includes(block.tagName) ||
        props.columnIndex === undefined ||
        !("children" in block) ||
        !block.children[props.columnIndex]
      ) {
        return;
      }
      this.throttledTakeSnapshot();
      this.edittedWithoutSave = true;
      // @ts-expect-error
      block.children[props.columnIndex].attributes[props.prop] =
        props.value as never;
    },

    resizeColumns(props: {
      sectionIndex: number;
      columnIndex: number;
      value: number;
    }) {
      this.throttledTakeSnapshot();

      const section = this.mjml?.children?.[0]?.children?.[props.sectionIndex];
      const columns = section?.children;
      if (
        !columns ||
        !columns[props.columnIndex] ||
        !columns[props.columnIndex + 1] ||
        !canResizePair(columns, props.columnIndex)
      ) {
        return;
      }

      // Column percentages are relative to the section's content box, not to
      // the body width.
      const contentWidth = getSectionContentWidth(section, this.getBodyWidth);
      const bounds = getColumnResizeBounds({
        columns,
        columnIndex: props.columnIndex,
        contentWidth,
      });

      // `canResizePair` guarantees at least 2 x MIN_COLUMN_PERCENT here, and the
      // bounds cap `width` at `available - MIN_COLUMN_PERCENT`, so the
      // neighbour keeps its floor and can never go negative.
      const width = clampColumnPercent(props.value, bounds);
      const neighbourWidth =
        getAvailablePercent(columns, props.columnIndex) - width;

      const applyWidth = (columnIndex: number, columnWidth: number) => {
        const column = columns[columnIndex];
        column.attributes.width = columnWidth + "%";
        const containerWidth = Math.round((columnWidth / 100) * contentWidth);

        for (const block of column.children) {
          block.attributes["containerWidth"] = containerWidth;
          if (block.tagName === "mj-loop") {
            for (const loopColumn of block.children) {
              for (const loopBlock of loopColumn.children) {
                loopBlock.attributes["containerWidth"] = Math.round(
                  (containerWidth * parseInt(loopColumn.attributes.width)) / 100
                );
              }
            }
          }
        }
      };

      applyWidth(props.columnIndex, width);
      applyWidth(props.columnIndex + 1, neighbourWidth);
    },

    reorderColumns(props: any): any {
      this.takeSnapshot();

      const columnArray =
        this.mjml.children[0].children[props.sectionIndex].children;

      // Move item in array from props.oldIndex to props.newIndex
      if (props.newIndex > columnArray.length) {
        props.newIndex = columnArray.length - 1;
      }

      if (props.newIndex !== columnArray.length) {
        columnArray.splice(
          props.newIndex,
          0,
          columnArray.splice(props.oldIndex, 1)[0]
        );
      }
    },

    reorderLoopColumns(props: any): any {
      const selectedBlock = this.getActiveBlock;

      if (!selectedBlock || selectedBlock.tagName !== "mj-loop") {
        return;
      }

      this.takeSnapshot();

      const columnArray = selectedBlock.children;

      // const columnArray =
      //   this.mjml.children[0].children[props.sectionIndex].children;

      // Move item in array from props.oldIndex to props.newIndex
      if (props.newIndex > columnArray.length) {
        props.newIndex = columnArray.length;
      }

      columnArray.splice(
        props.newIndex,
        0,
        columnArray.splice(props.oldIndex, 1)[0]
      );
    },

    getCorrectBlockIndexes(props: {
      oldIndex: number;
      newIndex: number;
      blocks: any;
    }) {
      const filterDesktop = props.blocks.filter((item: any) => {
        if (!item.attributes["css-class"]) {
          return item;
        }
        if (item.attributes["css-class"] !== "hide_on_desktop") {
          return item;
        }
      });
      const filterMobile = props.blocks.filter((item: any) => {
        if (!item.attributes["css-class"]) {
          return item;
        }
        if (item.attributes["css-class"] !== "hide_on_mobile") {
          return item;
        }
      });
      let oldOffset = 0;
      let newOffset = 0;
      if (this.isMobileFirstEnabled && this.blurHiddenElements) {
        if (this.isMobileView) {
          oldOffset =
            props.blocks.indexOf(filterMobile[props.oldIndex]) - props.oldIndex;
          if (filterMobile[props.newIndex]) {
            newOffset =
              props.blocks.indexOf(filterMobile[props.newIndex]) -
              props.newIndex;
          } else if (props.newIndex >= filterMobile.length) {
            newOffset = props.blocks.length - props.newIndex;
          }
        } else {
          oldOffset =
            props.blocks.indexOf(filterDesktop[props.oldIndex]) -
            props.oldIndex;
          if (filterDesktop[props.newIndex]) {
            newOffset =
              props.blocks.indexOf(filterDesktop[props.newIndex]) -
              props.newIndex;
          } else if (props.newIndex >= filterDesktop.length) {
            newOffset = props.blocks.length - props.newIndex;
          }
        }
      }
      const oldIndex = props.oldIndex + oldOffset;
      const newIndex = props.newIndex + newOffset;

      return {
        oldIndex,
        newIndex,
      };
    },

    isLastBlockInColumn(props: {
      sectionIndex: number;
      columnIndex: number;
      blockIndex: number;
    }) {
      const blocks =
        this.mjml.children[0].children[props.sectionIndex].children[
          props.columnIndex
        ].children;

      if (this.isMobileFirstEnabled && this.blurHiddenElements) {
        if (this.isMobileView) {
          const filterMobile = blocks.filter((item: any) => {
            if (!item.attributes["css-class"]) {
              return item;
            }
            if (item.attributes["css-class"] !== "hide_on_mobile") {
              return item;
            }
          });
          if (
            filterMobile.indexOf(blocks[props.blockIndex]) ===
            filterMobile.length - 1
          ) {
            return true;
          }
        } else {
          const filterDesktop = blocks.filter((item: any) => {
            if (!item.attributes["css-class"]) {
              return item;
            }
            if (item.attributes["css-class"] !== "hide_on_desktop") {
              return item;
            }
          });
          if (
            filterDesktop.indexOf(blocks[props.blockIndex]) ===
            filterDesktop.length - 1
          ) {
            return true;
          }
        }
      } else {
        if (props.blockIndex === blocks.length - 1) {
          return true;
        }
      }

      return false;
    },

    isFirstBlockInColumn(props: {
      sectionIndex: number;
      columnIndex: number;
      blockIndex: number;
    }) {
      const blocks =
        this.mjml.children[0].children[props.sectionIndex].children[
          props.columnIndex
        ].children;

      if (this.isMobileFirstEnabled && this.blurHiddenElements) {
        if (this.isMobileView) {
          const filterMobile = blocks.filter((item: any) => {
            if (!item.attributes["css-class"]) {
              return item;
            }
            if (item.attributes["css-class"] !== "hide_on_mobile") {
              return item;
            }
          });
          if (filterMobile.indexOf(blocks[props.blockIndex]) === 0) {
            return true;
          }
        } else {
          const filterDesktop = blocks.filter((item: any) => {
            if (!item.attributes["css-class"]) {
              return item;
            }
            if (item.attributes["css-class"] !== "hide_on_desktop") {
              return item;
            }
          });
          if (filterDesktop.indexOf(blocks[props.blockIndex]) === 0) {
            return true;
          }
        }
      } else {
        if (props.blockIndex === 0) {
          return true;
        }
      }

      return false;
    },

    getCorrectArrowBlockIndex(props: {
      oldIndex: number;
      newIndex: number;
      blocks: any;
    }) {
      const oldIndex = props.oldIndex;
      let newIndex = props.newIndex;

      if (this.isMobileFirstEnabled && this.blurHiddenElements) {
        if (
          this.isMobileView &&
          props.blocks[newIndex].attributes["css-class"] === "hide_on_mobile"
        ) {
          if (oldIndex > newIndex) {
            for (let i = newIndex; i > -1; i--) {
              if (
                props.blocks[i].attributes["css-class"] !== "hide_on_mobile"
              ) {
                newIndex = i;
                break;
              }
            }
          } else {
            for (let i = newIndex; i < props.blocks.length; i++) {
              if (
                props.blocks[i].attributes["css-class"] !== "hide_on_mobile"
              ) {
                newIndex = i;
                break;
              }
            }
          }
        }
        if (
          !this.isMobileView &&
          props.blocks[newIndex].attributes["css-class"] === "hide_on_desktop"
        ) {
          if (oldIndex > newIndex) {
            for (let i = newIndex; i > -1; i--) {
              if (
                props.blocks[i].attributes["css-class"] !== "hide_on_desktop"
              ) {
                newIndex = i;
                break;
              }
            }
          } else {
            for (let i = newIndex; i < props.blocks.length; i++) {
              if (
                props.blocks[i].attributes["css-class"] !== "hide_on_desktop"
              ) {
                newIndex = i;
                break;
              }
            }
          }
        }
      }

      return newIndex;
    },

    reorderBlocks(props: any): any {
      this.takeSnapshot();

      const blockArray =
        this.mjml.children[0].children[props.sectionIndex].children[
          props.columnIndex
        ].children;

      let oldIndex = props.oldIndex;
      let newIndex = props.newIndex;
      if (props.arrowMovement) {
        newIndex = this.getCorrectArrowBlockIndex({
          oldIndex: props.oldIndex,
          newIndex: props.newIndex,
          blocks: blockArray,
        });
      } else {
        const { oldIndex: oldI, newIndex: newI } = this.getCorrectBlockIndexes({
          oldIndex: props.oldIndex,
          newIndex: props.newIndex,
          blocks: blockArray,
        });
        oldIndex = oldI;
        newIndex = newI;
      }

      // Move item in array from oldIndex to newIndex
      if (newIndex > blockArray.length) {
        newIndex = blockArray.length - 1;
      }

      if (newIndex !== blockArray.length) {
        blockArray.splice(newIndex, 0, blockArray.splice(oldIndex, 1)[0]);
      }

      this.selectedSection = null;
      this.selectedBlock = {
        sectionIndex: props.sectionIndex,
        columnIndex: props.columnIndex,
        blockIndex: newIndex,
      };
    },

    reorderBlockChildren(props: any): any {
      const block =
        this.mjml.children[0].children[props.sectionIndex].children[
          props.columnIndex
        ].children[props.blockIndex];

      if (
        !block ||
        (block.tagName !== "mj-carousel" &&
          block.tagName !== "mj-social" &&
          block.tagName !== "mj-rating")
      ) {
        return;
      }
      this.takeSnapshot();

      const childrenArray = block.children;

      // Move item in array from props.oldIndex to props.newIndex
      if (props.newIndex > childrenArray.length) {
        props.newIndex = childrenArray.length - 1;
      }

      if (props.newIndex !== childrenArray.length) {
        childrenArray.splice(
          props.newIndex,
          0,
          // @ts-expect-error
          childrenArray.splice(props.oldIndex, 1)[0]
        );
        if (this.mjml.langs && this.mjml.langs.length > 0) {
          for (const lang of this.mjml.langs) {
            if (
              lang.translations[block.uid] &&
              lang.translations[block.uid].children
            ) {
              const children = lang.translations[block.uid].children;
              if (!children) {
                continue;
              }
              children.splice(
                props.newIndex,
                0,
                children.splice(props.oldIndex, 1)[0]
              );
            }
          }
        }
      }
    },

    reorderLoopBlockChildren(props: any): any {
      if (!this.mjml || !this.mjml.children || !this.mjml.children[0]) {
        return;
      }
      const base = this.mjml.children[0];

      if (
        !base.children ||
        !props.sectionIndex === undefined ||
        !base.children[props.sectionIndex]
      ) {
        return;
      }
      const section = base.children[props.sectionIndex];

      if (
        !section.children ||
        props.columnIndex === undefined ||
        !section.children[props.columnIndex]
      ) {
        return;
      }
      const column = section.children[props.columnIndex];

      if (
        !column.children ||
        props.blockIndex === undefined ||
        !column.children[props.blockIndex]
      ) {
        return;
      }
      const selectBlock = column.children[props.blockIndex];

      if (
        selectBlock.tagName !== "mj-loop" ||
        !selectBlock.children ||
        props.loopColumnIndex === undefined ||
        !selectBlock.children[props.loopColumnIndex]
      ) {
        return;
      }
      const childrenArray =
        selectBlock.children[props.loopColumnIndex].children;
      this.takeSnapshot();

      // Move item in array from props.oldIndex to props.newIndex
      if (props.newIndex > childrenArray.length) {
        props.newIndex = childrenArray.length;
      }

      childrenArray.splice(
        props.newIndex,
        0,
        childrenArray.splice(props.oldIndex, 1)[0]
      );
    },

    getReorderSectionVarinatsData(oldIndex: number, newIndex: number) {
      const sectionArray = this.mjml.children[0].children;
      const sectionVariants = this.mjml.sectionVariants;
      const foundVariantSections: string[] = [];
      let moveSectionVariantIndex = 0;
      let variantOffset = 0;

      if (sectionVariants && sectionVariants.length > 0) {
        sectionVariants.forEach((variant) => {
          variant.sections.forEach((sectionId, index) => {
            if (sectionId === sectionArray[oldIndex]?.uid) {
              foundVariantSections.push(...variant.sections);
              moveSectionVariantIndex = index;
            }
          });
        });

        sectionVariants.forEach((variant) => {
          variant.sections.forEach((sectionId) => {
            if (newIndex !== 0 && sectionId === sectionArray[newIndex].uid) {
              const activeVariantUid = variant.activeVariant;
              const activeVariant = sectionArray.find(
                (section) => section.uid === activeVariantUid
              );
              if (activeVariant) {
                const visibleSectionVarintIndex =
                  sectionArray.indexOf(activeVariant);
                const activeVariantIndex =
                  variant.sections.indexOf(activeVariantUid);
                if (
                  visibleSectionVarintIndex > newIndex ||
                  (newIndex < oldIndex &&
                    visibleSectionVarintIndex === newIndex)
                ) {
                  variantOffset = 0 - activeVariantIndex;
                } else {
                  variantOffset =
                    variant.sections.length - 1 - activeVariantIndex;
                }
              }
            }
          });
        });
      }
      return { foundVariantSections, moveSectionVariantIndex, variantOffset };
    },

    getCorrectSectionIndexes(props: { oldIndex: number; newIndex: number }) {
      const sectionArray = this.mjml.children[0].children;

      const filterDesktop = sectionArray.filter((item) => {
        if (!item.attributes["css-class"]) {
          return item;
        }
        if (item.attributes["css-class"] !== "hide_section_on_desktop") {
          return item;
        }
      });
      const filterMobile = sectionArray.filter((item) => {
        if (!item.attributes["css-class"]) {
          return item;
        }
        if (item.attributes["css-class"] !== "hide_section_on_mobile") {
          return item;
        }
      });
      let oldOffset = 0;
      let newOffset = 0;
      if (this.isMobileFirstEnabled && this.blurHiddenElements) {
        if (this.isMobileView) {
          oldOffset =
            sectionArray.indexOf(filterMobile[props.oldIndex]) - props.oldIndex;
          if (filterMobile[props.newIndex]) {
            newOffset =
              sectionArray.indexOf(filterMobile[props.newIndex]) -
              props.newIndex;
          } else if (props.newIndex >= filterMobile.length) {
            newOffset = sectionArray.length - props.newIndex;
          }
        } else {
          oldOffset =
            sectionArray.indexOf(filterDesktop[props.oldIndex]) -
            props.oldIndex;
          if (filterDesktop[props.newIndex]) {
            newOffset =
              sectionArray.indexOf(filterDesktop[props.newIndex]) -
              props.newIndex;
          } else if (props.newIndex >= filterDesktop.length) {
            newOffset = sectionArray.length - props.newIndex;
          }
        }
      }
      const oldIndex = props.oldIndex + oldOffset;
      const newIndex = props.newIndex + newOffset;

      return {
        oldIndex,
        newIndex,
      };
    },

    isLastSection(sectionIndex: number) {
      const sectionArray = this.mjml.children[0].children;
      if (this.isMobileFirstEnabled && this.blurHiddenElements) {
        if (this.isMobileView) {
          const filterMobile = sectionArray.filter((item) => {
            if (!item.attributes["css-class"]) {
              return item;
            }
            if (item.attributes["css-class"] !== "hide_section_on_mobile") {
              return item;
            }
          });
          if (
            filterMobile.indexOf(sectionArray[sectionIndex]) ===
            filterMobile.length - 1
          ) {
            return true;
          }
        } else {
          const filterDesktop = sectionArray.filter((item) => {
            if (!item.attributes["css-class"]) {
              return item;
            }
            if (item.attributes["css-class"] !== "hide_section_on_desktop") {
              return item;
            }
          });
          if (
            filterDesktop.indexOf(sectionArray[sectionIndex]) ===
            filterDesktop.length - 1
          ) {
            return true;
          }
        }
      } else {
        if (sectionIndex === sectionArray.length - 1) {
          return true;
        }
      }

      return false;
    },

    isFirstSection(sectionIndex: number) {
      const sectionArray = this.mjml.children[0].children;
      if (this.isMobileFirstEnabled && this.blurHiddenElements) {
        if (this.isMobileView) {
          const filterMobile = sectionArray.filter((item) => {
            if (!item.attributes["css-class"]) {
              return item;
            }
            if (item.attributes["css-class"] !== "hide_section_on_mobile") {
              return item;
            }
          });
          if (filterMobile.indexOf(sectionArray[sectionIndex]) === 0) {
            return true;
          }
        } else {
          const filterDesktop = sectionArray.filter((item) => {
            if (!item.attributes["css-class"]) {
              return item;
            }
            if (item.attributes["css-class"] !== "hide_section_on_desktop") {
              return item;
            }
          });
          if (filterDesktop.indexOf(sectionArray[sectionIndex]) === 0) {
            return true;
          }
        }
      } else {
        if (sectionIndex === 0) {
          return true;
        }
      }

      return false;
    },

    getCorrectArrowSectionIndex(props: { oldIndex: number; newIndex: number }) {
      const sectionArray = this.mjml.children[0].children;
      const oldIndex = props.oldIndex;
      let newIndex = props.newIndex;

      if (this.isMobileFirstEnabled && this.blurHiddenElements) {
        if (
          this.isMobileView &&
          sectionArray[newIndex].attributes["css-class"] ===
            "hide_section_on_mobile"
        ) {
          if (oldIndex > newIndex) {
            for (let i = newIndex; i > -1; i--) {
              if (
                sectionArray[i].attributes["css-class"] !==
                "hide_section_on_mobile"
              ) {
                newIndex = i;
                break;
              }
            }
          } else {
            for (let i = newIndex; i < sectionArray.length; i++) {
              if (
                sectionArray[i].attributes["css-class"] !==
                "hide_section_on_mobile"
              ) {
                newIndex = i;
                break;
              }
            }
          }
        }
        if (
          !this.isMobileView &&
          sectionArray[newIndex].attributes["css-class"] ===
            "hide_section_on_desktop"
        ) {
          if (oldIndex > newIndex) {
            for (let i = newIndex; i > -1; i--) {
              if (
                sectionArray[i].attributes["css-class"] !==
                "hide_section_on_desktop"
              ) {
                newIndex = i;
                break;
              }
            }
          } else {
            for (let i = newIndex; i < sectionArray.length; i++) {
              if (
                sectionArray[i].attributes["css-class"] !==
                "hide_section_on_desktop"
              ) {
                newIndex = i;
                break;
              }
            }
          }
        }
      }

      return newIndex;
    },

    reorderSections(props: {
      columnIndex?: number;
      newIndex: number;
      oldIndex: number;
      sectionIndex?: number;
      inVariants?: boolean;
      arrowMovement?: boolean;
    }) {
      this.takeSnapshot();
      this.sectionReordering = true;

      const sectionArray = this.mjml.children[0].children;

      if (props.inVariants) {
        sectionArray.splice(
          props.newIndex,
          0,
          sectionArray.splice(props.oldIndex, 1)[0]
        );
        nextTick(() => {
          this.sectionReordering = false;
        });
        return;
      }

      let oldIndex = props.oldIndex;
      let newIndex = props.newIndex;
      if (props.arrowMovement) {
        newIndex = this.getCorrectArrowSectionIndex(props);
      } else {
        const { oldIndex: oldI, newIndex: newI } =
          this.getCorrectSectionIndexes(props);
        oldIndex = oldI;
        newIndex = newI;
      }

      const { foundVariantSections, moveSectionVariantIndex, variantOffset } =
        this.getReorderSectionVarinatsData(oldIndex, newIndex);

      // Move item in array from oldIndex to newIndex
      if (newIndex < sectionArray.length) {
        if (foundVariantSections.length > 0) {
          for (const indexVariant in foundVariantSections) {
            const i = newIndex > oldIndex ? 0 : Number(indexVariant);
            const offset = i - moveSectionVariantIndex;
            sectionArray.splice(
              newIndex + variantOffset + i,
              0,
              sectionArray.splice(oldIndex + offset, 1)[0]
            );
          }

          this.selectedBlock = null;
          this.selectedSection = {
            sectionIndex:
              newIndex +
              variantOffset +
              (newIndex > oldIndex
                ? -(foundVariantSections.length - 1 - moveSectionVariantIndex)
                : moveSectionVariantIndex),
          };
        } else {
          sectionArray.splice(
            newIndex + variantOffset,
            0,
            sectionArray.splice(oldIndex, 1)[0]
          );

          this.selectedBlock = null;
          this.selectedSection = { sectionIndex: newIndex + variantOffset };
        }
      }
      nextTick(() => {
        this.sectionReordering = false;
      });
    },

    getCorrectBlockIndexesBetweenColumns(props: {
      oldIndex: number;
      newIndex: number;
      oldBlocks: any;
      newBlocks: any;
    }) {
      const oldFilterDesktop = props.oldBlocks.filter((item: any) => {
        if (!item.attributes["css-class"]) {
          return item;
        }
        if (item.attributes["css-class"] !== "hide_on_desktop") {
          return item;
        }
      });
      const oldFilterMobile = props.oldBlocks.filter((item: any) => {
        if (!item.attributes["css-class"]) {
          return item;
        }
        if (item.attributes["css-class"] !== "hide_on_mobile") {
          return item;
        }
      });
      const newFilterDesktop = props.newBlocks.filter((item: any) => {
        if (!item.attributes["css-class"]) {
          return item;
        }
        if (item.attributes["css-class"] !== "hide_on_desktop") {
          return item;
        }
      });
      const newFilterMobile = props.newBlocks.filter((item: any) => {
        if (!item.attributes["css-class"]) {
          return item;
        }
        if (item.attributes["css-class"] !== "hide_on_mobile") {
          return item;
        }
      });
      let oldOffset = 0;
      let newOffset = 0;
      if (this.isMobileFirstEnabled && this.blurHiddenElements) {
        if (this.isMobileView) {
          oldOffset =
            props.oldBlocks.indexOf(oldFilterMobile[props.oldIndex]) -
            props.oldIndex;
          if (newFilterMobile[props.newIndex]) {
            newOffset =
              props.newBlocks.indexOf(newFilterMobile[props.newIndex]) -
              props.newIndex;
          } else if (props.newIndex >= newFilterMobile.length) {
            newOffset = props.newBlocks.length - props.newIndex;
          }
        } else {
          oldOffset =
            props.oldBlocks.indexOf(oldFilterDesktop[props.oldIndex]) -
            props.oldIndex;
          if (newFilterDesktop[props.newIndex]) {
            newOffset =
              props.newBlocks.indexOf(newFilterDesktop[props.newIndex]) -
              props.newIndex;
          } else if (props.newIndex >= newFilterDesktop.length) {
            newOffset = props.newBlocks.length - props.newIndex;
          }
        }
      }

      const oldIndex = props.oldIndex + oldOffset;
      const newIndex = props.newIndex + newOffset;

      return {
        oldIndex,
        newIndex,
      };
    },

    reorderBlocksBetweenColumns(props: any): any {
      const base = this.mjml.children[0];
      this.takeSnapshot();
      if (
        base.children[props.oldSectionIndex] &&
        base.children[props.oldSectionIndex].children[props.oldColumnIndex]
      ) {
        const oldColumn =
          base.children[props.oldSectionIndex].children[props.oldColumnIndex];
        const newColumn =
          base.children[props.sectionIndex].children[props.columnIndex];
        const { oldIndex, newIndex } =
          this.getCorrectBlockIndexesBetweenColumns({
            oldIndex: props.oldIndex,
            newIndex: props.newIndex,
            oldBlocks: oldColumn.children,
            newBlocks: newColumn.children,
          });

        if (
          !base.children[props.oldSectionIndex].children[props.oldColumnIndex]
            .children[oldIndex] ||
          !JSON.stringify(
            base.children[props.oldSectionIndex].children[props.oldColumnIndex]
              .children[oldIndex]
          )
        ) {
          return;
        }

        const oldBlock = JSON.parse(
          JSON.stringify(
            base.children[props.oldSectionIndex].children[props.oldColumnIndex]
              .children[oldIndex]
          )
        );
        oldColumn.children.splice(oldIndex, 1);

        oldBlock.attributes["containerWidth"] = Math.round(
          (this.getBodyWidth * parseInt(newColumn.attributes.width)) / 100
        );
        if (oldBlock.tagName === "mj-loop") {
          oldBlock.children.forEach((loopCol: any) => {
            loopCol.children.forEach((block: any) => {
              block.attributes["containerWidth"] = Math.round(
                (oldBlock.attributes["containerWidth"] *
                  parseInt(loopCol.attributes.width)) /
                  100
              );
            });
          });
        }
        newColumn.children.splice(newIndex, 0, oldBlock);

        this.selectedSection = null;
        this.selectedBlock = {
          sectionIndex: props.sectionIndex,
          columnIndex: props.columnIndex,
          blockIndex: newIndex,
        };
      }
    },

    reorderLoopBlocksBetweenColumns(props: any): any {
      const selectBlock = this.getSelectedBlock;

      if (
        !selectBlock ||
        selectBlock.tagName !== "mj-loop" ||
        !selectBlock.children ||
        props.loopColumnIndex === undefined ||
        !selectBlock.children[props.oldLoopColumnIndex] ||
        !selectBlock.children[props.loopColumnIndex]
      ) {
        return;
      }
      const oldColumn = selectBlock.children[props.oldLoopColumnIndex];
      const newColumn = selectBlock.children[props.loopColumnIndex];

      if (
        !oldColumn.children[props.oldIndex] ||
        !JSON.stringify(oldColumn.children[props.oldIndex])
      ) {
        return;
      }
      this.takeSnapshot();

      const oldBlock = JSON.parse(
        JSON.stringify(oldColumn.children[props.oldIndex])
      );
      oldColumn.children.splice(props.oldIndex, 1);
      oldBlock.attributes["containerWidth"] = Math.round(
        (selectBlock.attributes["containerWidth"] *
          parseInt(newColumn.attributes.width)) /
          100
      );
      newColumn.children.splice(props.newIndex, 0, oldBlock);
    },

    setBodyBg(props: any): any {
      this.mjml.children[0].attributes["background-color"] = props.value;
    },

    updateBlockAttrGlobally(props: TopolBlockProps): any {
      if (
        props.block === "mj-class" ||
        props.block === "mj-preview" ||
        props.block === "mj-subject"
      ) {
        return;
      }
      this.throttledTakeSnapshot();

      this.edittedWithoutSave = true;
      // Get structures
      for (const section of this.mjml.children[0].children) {
        // Get columns
        for (const column of section.children) {
          // Get blocks
          for (const block of column.children) {
            if (block.tagName == props.block) {
              if (block.tagName == "mj-button" && props.prop === "hover") {
                block.hover = props.value;
              } else if (props.prop !== "hover") {
                block.attributes[props.prop as keyof typeof block.attributes] =
                  props.value as never;
              }
            }
            if (block.tagName === "mj-loop") {
              for (const loopCol of block.children) {
                for (const loopBlock of loopCol.children) {
                  if (loopBlock.tagName == props.block) {
                    if (
                      loopBlock.tagName == "mj-button" &&
                      props.prop === "hover"
                    ) {
                      loopBlock.hover = props.value;
                    } else if (props.prop !== "hover") {
                      loopBlock.attributes[
                        props.prop as keyof typeof loopBlock.attributes
                      ] = props.value as never;
                    }
                  }
                }
              }
            }
          }
        }
      }
      const blockAttr = this.mjml.attributes[props.block];
      if (Array.isArray(blockAttr) || !blockAttr) {
        this.mjml.attributes[props.block] = {
          [props.prop]: props.value,
        } as never;
        return;
      }
      blockAttr[props.prop as keyof typeof blockAttr] =
        props.value as (typeof blockAttr)[keyof typeof blockAttr];
    },

    updateUnchangedBlockAttrGlobally(props: TopolBlockProps): any {
      if (
        props.block === "mj-class" ||
        props.block === "mj-preview" ||
        props.block === "mj-subject"
      ) {
        return;
      }
      this.throttledTakeSnapshot();
      this.edittedWithoutSave = true;

      const baseAttr = this.mjml.attributes[props.block];
      const previousDefault = resolvePreviousDefault(
        baseAttr,
        props.prop,
        props.block
      );

      for (const section of this.mjml.children[0].children) {
        updateNodesRecursively(
          section as any,
          props.block,
          props.prop,
          props.value,
          previousDefault
        );
      }

      // Update the base attribute
      if (Array.isArray(baseAttr) || !baseAttr) {
        this.mjml.attributes[props.block] = {
          [props.prop]: props.value,
        } as never;
        return;
      }
      (baseAttr as Record<string, any>)[props.prop] = props.value;
    },

    updateBlockGroupAttribute(props: {
      group: string;
      prop: string;
      value: any;
    }): any {
      const selectBlock = this.getSelectedBlock as any;
      if (!selectBlock) return;
      this.throttledTakeSnapshot();
      this.edittedWithoutSave = true;
      const current = (selectBlock[props.group] || {}) as Record<string, any>;
      selectBlock[props.group] = { ...current, [props.prop]: props.value };
    },

    updateUnchangedBlockGroupAttrGlobally(props: {
      block: string;
      group: string;
      prop: string;
      value: any;
    }): any {
      this.throttledTakeSnapshot();
      this.edittedWithoutSave = true;

      const baseAttr = this.mjml.attributes[
        props.block as keyof typeof this.mjml.attributes
      ] as any;

      let previousDefault: any;
      if (
        baseAttr &&
        typeof baseAttr === "object" &&
        baseAttr[props.group] &&
        props.prop in baseAttr[props.group]
      ) {
        previousDefault = baseAttr[props.group][props.prop];
      } else {
        const blockDefaults =
          templateSettings[props.block as keyof typeof templateSettings];
        if (blockDefaults && typeof blockDefaults === "object") {
          const groupDefaults = (blockDefaults as Record<string, any>)[
            props.group
          ];
          if (groupDefaults && typeof groupDefaults === "object") {
            previousDefault = groupDefaults[props.prop];
          }
        }
      }

      const propagate = (node: any) => {
        if (!node) return;
        if (node.tagName === props.block && node[props.group]) {
          const groupVal = node[props.group] as Record<string, any>;
          if (
            groupVal[props.prop] === undefined ||
            groupVal[props.prop] === previousDefault
          ) {
            node[props.group] = { ...groupVal, [props.prop]: props.value };
          }
        }
        if (node.children) {
          for (const child of node.children) {
            propagate(child);
          }
        }
      };

      for (const section of this.mjml.children[0].children) {
        propagate(section);
      }

      if (
        !baseAttr ||
        Array.isArray(baseAttr) ||
        typeof baseAttr !== "object"
      ) {
        this.mjml.attributes[props.block as keyof typeof this.mjml.attributes] =
          {
            [props.group]: { [props.prop]: props.value },
          } as never;
        return;
      }
      const currentGroup =
        baseAttr[props.group] && typeof baseAttr[props.group] === "object"
          ? baseAttr[props.group]
          : {};
      baseAttr[props.group] = { ...currentGroup, [props.prop]: props.value };
    },

    updateSectionAttrGlobally(props: {
      prop: "background-color" | "gradient";
      value: string;
    }): any {
      this.throttledTakeSnapshot();

      this.edittedWithoutSave = true;

      if (!this.mjml.attributes["mj-class"]) {
        this.mjml.attributes["mj-class"] = {
          name: "section",
        };
      }
      this.mjml.attributes["mj-class"][props.prop] = props.value;
    },

    setBaseAttr(props: TopolBlockProps): any {
      this.takeSnapshot();

      this.edittedWithoutSave = true;
      const blockAttr = this.mjml.attributes[props.block];
      if (blockAttr) {
        blockAttr[props.prop as keyof typeof blockAttr] =
          props.value as (typeof blockAttr)[keyof typeof blockAttr];
      } else {
        this.mjml.attributes[props.block] = {
          [props.prop]: props.value,
        } as never;
      }
    },

    setBaseAttrTranslation(props: TopolBlockProps): any {
      this.takeSnapshot();

      this.edittedWithoutSave = true;
      if (
        this.mjml.langs &&
        this.mjml.langs.length > 0 &&
        emailTranslatableBlockTags.includes(
          props.block as ITranslatableBlockTag
        ) &&
        props.prop === "text"
      ) {
        let translation = this.mjml.langs.find(
          (lang) => lang.key === this.langToShow
        );
        if (!translation) {
          const defaultLang = this.mjml.langs.find((lang) => lang.primary);
          if (!defaultLang) {
            this.mjml.langs[0].primary = true;
            translation = this.mjml.langs[0];
            this.emitLanguageCallback("onPrimaryLanguageChanged", [
              this.mjml.langs[0].key,
              langsToMutations(this.mjml.langs),
            ]);
          } else {
            translation = defaultLang;
          }
          this.langToShow = translation.key;
          this.emitLanguageCallback("onLanguageSelected", [this.langToShow]);
        }
        if (translation.translations[props.block]) {
          translation.translations[props.block].content = props.value as string;
        } else {
          translation.translations[props.block] = {
            content: props.value as string,
          };
        }
      } else {
        const blockAttr = this.mjml.attributes[props.block];
        if (blockAttr) {
          blockAttr[props.prop as keyof typeof blockAttr] =
            props.value as (typeof blockAttr)[keyof typeof blockAttr];
        } else {
          this.mjml.attributes[props.block] = {
            [props.prop]: props.value,
          } as never;
        }
      }
    },

    setStyle(props: TopolTagProps): any {
      this.takeSnapshot();
      let prevValue = undefined;

      this.edittedWithoutSave = true;
      if (Array.isArray(this.mjml.style)) {
        // @ts-expect-error
        this.mjml.style = {};
      }

      if (this.mjml.style !== undefined && !this.mjml.style[props.tag]) {
        // @ts-expect-error
        this.mjml.style[props.tag] = {};
      }

      if (this.mjml.style !== undefined) {
        if (props.value === undefined) {
          const tagStyle = this.mjml.style[props.tag];
          if (
            tagStyle &&
            tagStyle[props.attr as keyof typeof tagStyle] !== undefined
          ) {
            delete tagStyle[props.attr as keyof typeof tagStyle];
          }
        } else {
          const tagStyle = this.mjml.style[props.tag];
          if (tagStyle) {
            prevValue = tagStyle[props.attr as keyof typeof tagStyle];
            tagStyle[props.attr as keyof typeof tagStyle] =
              props.value as typeof prevValue;
          }
        }

        if (props.tag === "p") {
          if (this.mjml.style.li === undefined) {
            // @ts-expect-error
            this.mjml.style.li = {};
          }
          type LiStyle = keyof typeof this.mjml.style.li;
          if (
            this.mjml.style.li[props.attr as LiStyle] === undefined ||
            this.mjml.style.li[props.attr as LiStyle] === prevValue
          ) {
            this.mjml.style.li[props.attr as LiStyle] = props.value as never;
          }
        }
      }
    },

    addColumn(): any {
      const section = this.getSelectedSectionByIndex();
      if (!section) {
        return;
      }
      this.takeSnapshot();
      this.edittedWithoutSave = true;

      const column = JSON.parse(
        JSON.stringify(section.children[section.children.length - 1])
      );

      // Change uids of children
      for (const block of column.children) {
        const oldUid = block.uid;
        block.uid = shortid.generate();
        this.duplicateBlockTranslations(block, oldUid);
      }
      column.uid = shortid.generate();
      section.children.push(column);

      // recount widths
      const newWidthPercent = 100 / section.children.length + "%";
      const newWidthPx = this.getBodyWidth / section.children.length;
      for (const col of section.children) {
        col["attributes"]["width"] = newWidthPercent;
        // Set container widths
        for (const block of col.children) {
          block.attributes["containerWidth"] = newWidthPx;
          if (block.tagName === "mj-loop") {
            for (const loopCol of block.children) {
              for (const loopBlock of loopCol.children) {
                loopBlock.attributes["containerWidth"] = Math.round(
                  (newWidthPx * parseInt(loopCol.attributes["width"])) / 100
                );
              }
            }
          }
        }
      }
    },

    removeColumn(): any {
      const section = this.getSelectedSectionByIndex();
      if (!section) {
        return;
      }
      this.takeSnapshot();
      this.edittedWithoutSave = true;
      const column = section.children.pop();
      if (this.mjml.langs) {
        column.children.forEach((block: any) => {
          this.deleteBlockTranslation(block);
        });
      }

      // recount widths
      const newWidthPercent = 100 / section.children.length + "%";
      const newWidthPx = this.getBodyWidth / section.children.length;
      for (const col of section.children) {
        col["attributes"]["width"] = newWidthPercent;
        // Set container widths
        for (const block of col.children) {
          block.attributes["containerWidth"] = newWidthPx;
          if (block.tagName === "mj-loop") {
            for (const loopCol of block.children) {
              for (const loopBlock of loopCol.children) {
                loopBlock.attributes["containerWidth"] = Math.round(
                  (newWidthPx * parseInt(loopCol.attributes["width"])) / 100
                );
              }
            }
          }
        }
      }
    },

    addBlockColumn(): any {
      const selectedBlock = this.getSelectedBlock;
      if (
        !selectedBlock ||
        selectedBlock.tagName !== "mj-loop" ||
        !selectedBlock.children ||
        selectedBlock.children.length >= 8
      ) {
        return;
      }
      this.takeSnapshot();

      this.edittedWithoutSave = true;
      const column = JSON.parse(
        JSON.stringify(
          selectedBlock.children[selectedBlock.children.length - 1]
        )
      );
      column.children = [];
      column.uid = shortid.generate();
      selectedBlock.children.push(column);

      // recount widths
      const newWidthPercent = 100 / selectedBlock.children.length + "%";
      const newWidthPx = Math.round(
        selectedBlock.attributes["containerWidth"] /
          selectedBlock.children.length
      );
      for (const column of selectedBlock.children) {
        column["attributes"]["width"] = newWidthPercent;
        for (const block of column.children) {
          block.attributes["containerWidth"] = newWidthPx;
        }
      }
    },

    removeBlockColumn(): any {
      const selectedBlock = this.getSelectedBlock;
      if (
        !selectedBlock ||
        selectedBlock.tagName !== "mj-loop" ||
        !selectedBlock.children ||
        selectedBlock.children.length < 2
      ) {
        return;
      }

      this.takeSnapshot();

      this.edittedWithoutSave = true;
      // add children to the previous block
      if (
        selectedBlock.children[selectedBlock.children.length - 1].children
          .length > 0
      ) {
        selectedBlock.children[selectedBlock.children.length - 2].children.push(
          ...selectedBlock.children[selectedBlock.children.length - 1].children
        );
      }
      selectedBlock.children.pop();

      // recount widths
      const newWidthPercent = 100 / selectedBlock.children.length + "%";
      const newWidthPx = Math.round(
        selectedBlock.attributes["containerWidth"] /
          selectedBlock.children.length
      );
      for (const column of selectedBlock.children) {
        column["attributes"]["width"] = newWidthPercent;
        for (const block of column.children) {
          block.attributes["containerWidth"] = newWidthPx;
        }
      }
    },

    setActiveLoopMergetag(mergetag: IMergeTag): any {
      const selectedBlock = this.getSelectedBlock;
      if (
        !this.selectedBlock ||
        !selectedBlock ||
        selectedBlock.tagName !== "mj-loop" ||
        selectedBlock.children.length < 1 ||
        !mergetag.childrenProperties
      ) {
        return;
      }
      this.takeSnapshot();
      this.edittedWithoutSave = true;
      selectedBlock.forMergeTag = mergetag;
      if (selectedBlock.children.length > 0) {
        for (let i = 0; i < selectedBlock.children.length; i++) {
          selectedBlock.children[i].children = [];
        }
      }
      const pxWidth = Math.round(
        (selectedBlock.attributes["containerWidth"] *
          parseInt(selectedBlock.children[0].attributes["width"])) /
          100
      );
      for (const childerTag of mergetag.childrenProperties) {
        if (childerTag.type === "image") {
          const imgAttrs: Record<string, any> = {
            containerWidth: pxWidth,
            src: childerTag.value,
            alt: "",
            href: "",
            padding: this.getBaseAttrString("mj-image", "padding") || "0px",
            "fluid-on-mobile":
              this.getBaseAttrString("mj-image", "fluid-on-mobile") || "false",
          };
          const imgBorderRadius = this.getBaseAttrString(
            "mj-image",
            "border-radius"
          );
          if (imgBorderRadius) imgAttrs["border-radius"] = imgBorderRadius;

          selectedBlock.children[0].children.push({
            tagName: "mj-image",
            uid: shortid.generate(),
            attributes: imgAttrs as any,
          });
          continue;
        }
        if (childerTag.type === "button") {
          const block = resolveBlock("button");

          if (!block) {
            continue;
          }

          // @ts-expect-error TODO: type block
          const objectData: TopolBlock = block.getData(
            this.$state,
            this.selectedBlock
          );
          if (!objectData || objectData.tagName !== "mj-button") {
            continue;
          }
          objectData.attributes["href"] = childerTag.value;
          objectData.attributes["containerWidth"] = pxWidth;
          objectData.content = "Click here";
          selectedBlock.children[0].children.push(objectData);
          continue;
        }
        selectedBlock.children[0].children.push({
          tagName: "mj-text",
          uid: shortid.generate(),
          attributes: {
            containerWidth: pxWidth,
            color: this.getBaseAttrString("mj-text", "color") || "#000000",
            // The global mj-text defaults carry no font-size (a regular text
            // block sizes its content inline), so loop text gets a fixed default.
            "font-size": "15px",
            "font-family":
              this.getBaseAttrString("mj-text", "font-family") ||
              "Helvetica, Arial, sans-serif",
            "line-height":
              this.getBaseAttrString("mj-text", "line-height") || undefined,
            padding: this.getBaseAttrString("mj-text", "padding") || "0px",
          },
          content: childerTag.value,
        });
      }
    },

    mapApiItemToBlockItem(props: {
      item: any;
      typeName: string;
      inheritedButton: any;
    }): any | null {
      const selectedBlock = this.getSelectedBlock as TopolFeedBlock | null;
      if (!selectedBlock) {
        return null;
      }

      const replaceTextInTinyMCEField = (
        target: any,
        attributeName: string,
        newValue: string,
        defaultValue: string | undefined
      ) => {
        if ((newValue === undefined || newValue === "") && defaultValue) {
          newValue = defaultValue;
        }

        target[attributeName] = newValue;

        if (attributeName !== "image" && attributeName !== "href") {
          const doc = new DOMParser().parseFromString(newValue, "text/html");
          target[`raw-${attributeName}`] = doc.body.textContent || "";
        }
      };

      const item = props.item;
      const apiBlocksStore = useApiBlocksStore();
      const blockProperties = apiBlocksStore.blocks.get(props.typeName);
      if (!blockProperties) {
        return null;
      }

      const attributes = selectedBlock.attributes;
      const thisItem: any = {
        id: item.id,
        name: item.name,
      };

      Object.keys(blockProperties.blockStructure).forEach((key) => {
        if (key === "layout") {
          return;
        }
        if (key === "button") {
          thisItem[key] = props.inheritedButton;
          return;
        }
        let keyValue = item[key];
        if (key === "price-before" && item.price_before) {
          keyValue = formatPrice(
            item.price_before,
            item.currency,
            attributes["eur-shape"],
            attributes["eur-position"]
          );
        }
        if (key === "price" && item.price_vat) {
          keyValue = formatPrice(
            item.price_vat,
            item.currency,
            attributes["eur-shape"],
            attributes["eur-position"]
          );
        }
        if (key === "published-at" && keyValue) {
          keyValue = format(new Date(keyValue * 1000), "dd. MM. yyyy");
        }
        replaceTextInTinyMCEField(
          thisItem,
          key,
          keyValue,
          blockProperties.blockStructure[key].defaultValue
        );
      });

      if ("image" in thisItem) {
        if (thisItem["raw-title"]) {
          thisItem["image-alt"] = thisItem["raw-title"].replace(
            // eslint-disable-next-line no-control-regex
            /[\x00-\x1F\x7F]/u,
            ""
          );
        } else if (thisItem.title) {
          thisItem["image-alt"] = thisItem.title.replace(
            // eslint-disable-next-line no-control-regex
            /[\x00-\x1F\x7F]/u,
            ""
          );
        }
      }

      thisItem[`${props.typeName}_id`] = item.id;

      if (item[`${props.typeName}_feed_id`]) {
        thisItem[`${props.typeName}_feed_id`] =
          item[`${props.typeName}_feed_id`];
      } else {
        thisItem[`${props.typeName}_feed_id`] = item.feed_id;
      }

      return thisItem;
    },

    selectApiItems(props: any): any {
      const selectedBlock = this.getSelectedBlock as TopolFeedBlock | null;
      if (!selectedBlock) {
        return;
      }

      if (
        selectedBlock.tagName === "mj-feed-block" &&
        selectedBlock.mode !== "dynamic" &&
        !this.isOnPrimaryLang()
      ) {
        return;
      }

      this.takeSnapshot();

      const items = props.items;
      const apiBlocksStore = useApiBlocksStore();
      const blockProperties = apiBlocksStore.blocks.get(props.typeName);
      if (!blockProperties) {
        return;
      }

      const primary =
        selectedBlock.tagName === "mj-feed-block"
          ? this._resolvePrimaryFeedItems(selectedBlock)
          : selectedBlock.items;

      const inheritedButton =
        primary[primary.length - 1]?.button ||
        this._findSiblingFeedButtonText(selectedBlock) ||
        blockProperties.blockStructure["button"]?.defaultValue;
      const newItems: any[] = [];
      items.forEach((item: any) => {
        const existing = primary.find((i: any) => i.id === item.id);
        if (existing) {
          newItems.push(existing);
          return;
        }
        const mapped = this.mapApiItemToBlockItem({
          item,
          typeName: props.typeName,
          inheritedButton,
        });
        if (mapped) {
          newItems.push(mapped);
        }
      });

      if (selectedBlock.tagName === "mj-feed-block") {
        // indexMap[i] = old index of the item now at position i; -1 for
        // brand-new ids. Same pattern as reorderFeedItems — one map built
        // once, then applied to every non-primary override array.
        const oldIndexById = new Map(
          primary.map((item: any, i: number) => [item.id, i])
        );
        const indexMap: number[] = newItems.map(
          (item: any) => oldIndexById.get(item.id) ?? -1
        );

        primary.splice(0, primary.length, ...newItems);
        for (const lang of this.mjml.langs ?? []) {
          if (lang.primary) continue;
          const oldOverrides = (lang.translations[selectedBlock.uid] as any)
            ?.items;
          if (!Array.isArray(oldOverrides)) continue;
          (lang.translations[selectedBlock.uid] as any).items = indexMap.map(
            (i) => oldOverrides[i] ?? null
          );
        }
        // Seed sibling button text into every non-primary lang for slots
        // that don't already have a button override.
        this._seedSiblingButtonInTranslations(selectedBlock, newItems);
      } else {
        selectedBlock.items = newItems;
      }
      this.edittedWithoutSave = true;
    },

    addApiBlockDynamicItem(props: any): any {
      const selectedBlock = this.getSelectedBlock as TopolFeedBlock | null;
      if (!selectedBlock) {
        return;
      }

      const mergeTagsStrore = useMergeTagsStore();
      const MTSyntax = mergeTagsStrore.MTSyntax;

      const apiBlocksStore = useApiBlocksStore();
      const blockProperties = apiBlocksStore.blocks.get(props.typeName);
      if (!blockProperties) {
        return;
      }

      const apiStructure = blockProperties.apiStructure;
      const primaryLang = this.mjml.langs?.find((l) => l.primary);
      const thisItem: any = {};
      Object.keys(blockProperties.blockStructure).forEach((key) => {
        if (key === "layout") {
          return;
        }
        if (key === "button") {
          // Prefer sibling preset (applyToAll) for primary lang, fall
          // back to the block's default value.
          thisItem[key] =
            this._findSiblingFeedButtonText(selectedBlock, primaryLang?.key) ||
            blockProperties.blockStructure[key].defaultValue;
          return;
        }
        if (key === "price-before" && apiStructure?.price_before) {
          thisItem[key] =
            MTSyntax.start + apiStructure.price_before + MTSyntax.end;
          return;
        }
        if (key === "price" && apiStructure?.price_vat) {
          thisItem[key] =
            MTSyntax.start + apiStructure.price_vat + MTSyntax.end;
          return;
        }
        if (apiStructure?.[key]) {
          thisItem[key] = MTSyntax.start + apiStructure[key] + MTSyntax.end;
          return;
        }
        thisItem[key] = MTSyntax.start + key + MTSyntax.end;
      });
      if ("image" in thisItem) {
        if (thisItem.title) {
          thisItem["image-alt"] = thisItem.title;
        }
      }
      const primary = this._resolvePrimaryFeedItems(selectedBlock);
      primary.push(thisItem);
      // Seed sibling button text into non-primary translations so the
      // dynamic template item reflects per-lang CTAs immediately.
      this._seedSiblingButtonInTranslations(selectedBlock, primary);
    },

    // When applyToAll is on, find a sibling feed block of the same
    // blockName whose first item has a button text in the given language.
    // Defaults to the currently active language.
    _findSiblingFeedButtonText(
      block: any,
      langKey?: string
    ): string | undefined {
      if (block?.tagName !== "mj-feed-block" || !block.blockName) {
        return undefined;
      }
      if (!this.mjml.feedBlocksApplyToAll?.[block.blockName]) {
        return undefined;
      }
      const effectiveLang = langKey ?? this.langToShow;
      for (const section of this.mjml.children?.[0]?.children ?? []) {
        for (const column of section.children ?? []) {
          for (const other of column.children ?? []) {
            if (other === block) continue;
            if (other.tagName !== "mj-feed-block") continue;
            if (other.blockName !== block.blockName) continue;
            const items = getFeedItemsForLang(
              other,
              this.mjml.langs,
              effectiveLang
            );
            if (items.length > 0 && items[0]?.button) return items[0].button;
          }
        }
      }
      return undefined;
    },

    // After a primary-side set/replace, propagate the sibling-inherited
    // button text into every non-primary lang where the translation
    // differs from primary — so a fresh block shows the translated CTA
    // immediately in every mutation, not just the active one.
    _seedSiblingButtonInTranslations(block: any, items: any[]): void {
      if (block?.tagName !== "mj-feed-block" || !this.mjml.langs?.length)
        return;
      if (!this.mjml.feedBlocksApplyToAll?.[block.blockName]) return;
      const primaryLang = this.mjml.langs.find((l) => l.primary);
      const primaryBtn = this._findSiblingFeedButtonText(
        block,
        primaryLang?.key
      );
      for (const lang of this.mjml.langs) {
        if (lang.primary) continue;
        const langBtn = this._findSiblingFeedButtonText(block, lang.key);
        if (!langBtn || langBtn === primaryBtn) continue;
        if (!lang.translations[block.uid]) lang.translations[block.uid] = {};
        const blockTrans = lang.translations[block.uid] as any;
        let overrides: (any | null)[] = Array.isArray(blockTrans.items)
          ? blockTrans.items
          : [];
        items.forEach((it: any, i: number) => {
          // Skip slots that already have a button override for this lang.
          if (overrides[i]?.button !== undefined) return;
          overrides = this._upsertFeedItemFieldOverride(
            overrides,
            i,
            it.id,
            "button",
            langBtn
          );
        });
        if (overrides.length > 0) blockTrans.items = overrides;
      }
    },

    isOnPrimaryLang(): boolean {
      if (!this.langToShow || !this.mjml.langs || this.mjml.langs.length === 0)
        return true;
      const primary = this.mjml.langs.find((l) => l.primary);
      return this.langToShow === primary?.key;
    },

    // return primary or default items
    _resolvePrimaryFeedItems(block: any): any[] {
      if (!this.mjml.langs || this.mjml.langs.length === 0) {
        if (!Array.isArray(block.items)) block.items = [];
        return block.items;
      }
      const primary = this.mjml.langs.find((l) => l.primary);
      if (!primary) {
        if (!Array.isArray(block.items)) block.items = [];
        return block.items;
      }
      if (!primary.translations[block.uid])
        primary.translations[block.uid] = {};
      const blockTrans = primary.translations[block.uid] as any;
      if (!Array.isArray(blockTrans.items)) {
        blockTrans.items = Array.isArray(block.items)
          ? block.items.map((i: any) => ({ ...i }))
          : [];
      }
      return blockTrans.items;
    },

    _writeFeedItemField(
      block: any,
      itemIndex: number,
      field: string,
      value: any
    ): void {
      if (!block || block.tagName !== "mj-feed-block") return;
      const primary = this._resolvePrimaryFeedItems(block);
      if (itemIndex < 0 || itemIndex >= primary.length) return;
      const primaryItem = primary[itemIndex];
      if (!primaryItem) return;

      this.edittedWithoutSave = true;

      if (this.isOnPrimaryLang()) {
        primary[itemIndex] = { ...primaryItem, [field]: value };
        return;
      }

      if (!this.mjml.langs || this.mjml.langs.length === 0) return;
      const lang = this.mjml.langs.find((l) => l.key === this.langToShow);
      if (!lang) return;
      if (!lang.translations[block.uid]) lang.translations[block.uid] = {};
      const blockTrans = lang.translations[block.uid] as any;
      blockTrans.items = this._upsertFeedItemFieldOverride(
        blockTrans.items,
        itemIndex,
        primaryItem.id,
        field,
        value
      );
    },

    _upsertFeedItemFieldOverride(
      translationItems: any[] | undefined,
      index: number,
      primaryItemId: string | number | undefined,
      field: string,
      value: any
    ): (any | null)[] {
      const items: any[] = translationItems ? [...translationItems] : [];

      const isEmpty = value === "" || value == null;
      const existing = items[index];

      if (existing) {
        const entry: any = { ...existing };
        if (isEmpty) {
          delete entry[field];
          items[index] = entry;
        } else {
          entry[field] = value;
          items[index] = entry;
        }
      } else if (!isEmpty) {
        items[index] = { id: primaryItemId, [field]: value };
      }
      return items;
    },

    updateFeedItemField(props: {
      index: number;
      field: string;
      value: any;
    }): void {
      const block = this.getSelectedBlock;
      if (!block || block.tagName !== "mj-feed-block") return;
      this.throttledTakeSnapshot();
      this._writeFeedItemField(block, props.index, props.field, props.value);
    },

    removeFeedItem(props: { index: number }): void {
      const block = this.getSelectedBlock as TopolFeedBlock | null;
      if (!block || block.tagName !== "mj-feed-block") return;
      if (!this.isOnPrimaryLang() && block.mode !== "dynamic") {
        return;
      }
      const primary = this._resolvePrimaryFeedItems(block);
      if (props.index < 0 || props.index >= primary.length) return;

      this.takeSnapshot();
      this.edittedWithoutSave = true;

      primary.splice(props.index, 1);

      for (const lang of this.mjml.langs ?? []) {
        if (lang.primary) continue;
        const items = (lang.translations[block.uid] as any)?.items;
        if (Array.isArray(items)) items.splice(props.index, 1);
      }
    },

    replaceFeedItemAt(props: {
      index: number;
      item: any;
      typeName: string;
    }): void {
      const block = this.getSelectedBlock as TopolFeedBlock | null;
      if (!block || block.tagName !== "mj-feed-block") return;
      const primary = this._resolvePrimaryFeedItems(block);
      if (props.index < 0 || props.index >= primary.length) return;

      const apiBlocksStore = useApiBlocksStore();
      const blockProperties = apiBlocksStore.blocks.get(props.typeName);
      if (!blockProperties) return;

      const oldItems = getFeedItemsForLang(
        block,
        this.mjml.langs,
        this.langToShow
      );
      const oldItem = oldItems[props.index];
      const inheritedButton =
        oldItem?.button ||
        this._findSiblingFeedButtonText(block) ||
        blockProperties.blockStructure["button"]?.defaultValue;
      const mapped = this.mapApiItemToBlockItem({
        item: props.item,
        typeName: props.typeName,
        inheritedButton,
      });
      if (!mapped) return;

      this.takeSnapshot();
      this.edittedWithoutSave = true;

      if (this.isOnPrimaryLang()) {
        primary[props.index] = mapped;
        if (
          this.mjml.langs?.length &&
          oldItem?.id != null &&
          oldItem.id !== mapped.id
        ) {
          for (const lang of this.mjml.langs ?? []) {
            if (lang.primary) continue;
            const blockTrans = lang.translations[block.uid] as any;
            if (!Array.isArray(blockTrans?.items)) continue;
            if (props.index < 0 || props.index >= blockTrans.items.length)
              continue;
            const entry = blockTrans.items[props.index];
            if (entry && entry.id === oldItem.id) {
              const next = [...blockTrans.items];
              const buttonText = entry.button;
              next[props.index] = buttonText
                ? { id: mapped.id, button: buttonText }
                : null;
              blockTrans.items = next;
            }
          }
        }
        // Seed sibling button text into non-primary translations for this
        // slot — keeps a freshly replaced product carrying the per-lang CTA.
        this._seedSiblingButtonInTranslations(block, primary);
        return;
      }

      if (!this.mjml.langs?.length) return;
      const lang = this.mjml.langs.find((l) => l.key === this.langToShow);
      if (!lang) return;
      if (!lang.translations[block.uid]) lang.translations[block.uid] = {};
      const blockTrans = lang.translations[block.uid] as any;
      if (!Array.isArray(blockTrans.items)) blockTrans.items = [];
      blockTrans.items[props.index] = { ...mapped };
    },

    reorderFeedItems(props: { uid: string; newOrder: any[] }): void {
      const block = this.getSelectedBlock as TopolFeedBlock | null;
      if (!block || block.uid !== props.uid) return;
      if (block.tagName !== "mj-feed-block") return;

      if (!this.isOnPrimaryLang()) {
        return;
      }
      const primary = this._resolvePrimaryFeedItems(block);
      this.takeSnapshot();
      this.edittedWithoutSave = true;

      // Build the permutation: newPosition[i] = oldIndex of the item now
      // at position i. Apply the same permutation to primary and to every
      // non-primary lang's overrides array.
      const oldIndexById = new Map(
        primary.map((item: any, i: number) => [item.id, i])
      );
      const permutation: number[] = props.newOrder.map(
        (d: any) => oldIndexById.get(d?.id) ?? -1
      );
      if (
        permutation.some((i) => i < 0) ||
        permutation.length !== primary.length
      )
        return;

      const reorder = <T>(arr: T[]): T[] => permutation.map((i) => arr[i]);
      primary.splice(0, primary.length, ...reorder(primary));

      for (const lang of this.mjml.langs ?? []) {
        if (lang.primary) continue;
        const items = (lang.translations[block.uid] as any)?.items;
        if (Array.isArray(items)) {
          (lang.translations[block.uid] as any).items = reorder(items);
        }
      }
    },

    resetFeedItems(): void {
      const block = this.getSelectedBlock as TopolFeedBlock | null;
      if (!block || block.tagName !== "mj-feed-block") return;
      this.takeSnapshot();
      this.edittedWithoutSave = true;
      if (this.mjml.langs?.length) {
        for (const lang of this.mjml.langs) {
          const blockTrans = lang.translations[block.uid] as any;
          if (blockTrans && Array.isArray(blockTrans.items)) {
            delete blockTrans.items;
          }
        }
      }
      block.items = [];
    },

    fixAnchorTagStyle() {
      this.edittedWithoutSave = true;

      if (this.mjml.style?.a) {
        const color = this.mjml.style.a.color;
        // @ts-expect-error
        this.mjml.style.a = {};

        if (color) {
          this.mjml.style.a = {
            color: color,
          };
        } else {
          // @ts-expect-error
          this.mjml.style.a = {};
        }
      }
    },

    setIsMobileView(val: boolean): any {
      let cssBlockClass = "hide_on_desktop";
      let cssSectionClass = "hide_section_on_desktop";

      if (val) {
        cssBlockClass = "hide_on_mobile";
        cssSectionClass = "hide_section_on_mobile";
      }

      let hiddenClassesFound = 0;
      let totalBlocks = 0;

      let hiddenStructuresFound = 0;
      let totalStructures = 0;

      // Get structures
      for (const section of this.mjml.children[0].children) {
        totalStructures++;
        if (section.attributes["css-class"] === cssSectionClass) {
          hiddenStructuresFound++;
        }

        // Get columns
        for (const column of section.children) {
          // Get blocks
          for (const block of column.children) {
            totalBlocks++;
            if (block.attributes["css-class"] === cssBlockClass) {
              hiddenClassesFound++;
            }
          }
        }
      }

      if (
        totalBlocks === hiddenClassesFound ||
        hiddenStructuresFound === totalStructures
      ) {
        this.blurHiddenElements = false;

        const message = {
          type: "callback",
          action: "onBlocksAndStructuresVisibilityChange",
          data: {
            args: [false],
          },
        };

        window.parent.postMessage(message, "*");
      }

      this.isMobileView = val;
    },

    updateCustomBlockContent(payload: any): any {
      const selectedBlock = this.getSelectedBlock;
      if (!selectedBlock) {
        return;
      }
      const wasTranslated = this.updateBlockContentTranslation(
        { prop: "content", value: payload.content },
        selectedBlock
      );
      if (wasTranslated) {
        return;
      }
      // @ts-expect-error
      selectedBlock.content = payload.content;
    },

    setTranslationOfWholeTemplate(
      translations: {
        uid: string;
        content: string;
      }[],
      targetLang: string | undefined,
      skipSnapshot: boolean
    ): any {
      if (!skipSnapshot) {
        this.takeSnapshot();
      }
      if (targetLang && this.mjml.langs?.length) {
        const target = this.mjml.langs.find((lang) => lang.key === targetLang);
        if (!target) return;
        if (!target.translations) {
          target.translations = {};
        }
        this.edittedWithoutSave = true;
        translations.forEach((item) => {
          if (item.uid === "mj-preview" || item.uid === "mj-subject") {
            target.translations[item.uid] = { content: item.content };
            return;
          }
          const block = this.getSegmentByUid(item.uid);
          if (
            block &&
            (block.tagName === "mj-text" || block.tagName === "mj-button")
          ) {
            target.translations[item.uid] = { content: item.content };
          }
        });
        return;
      }
      if (!this.mjml.langs?.length) {
        translations.forEach((item) => {
          if (item.uid === "mj-preview" || item.uid === "mj-subject") {
            this.setBaseAttrTranslation({
              block: item.uid,
              prop: "text",
              value: item.content,
            });
            return;
          }
          const block = this.getSegmentByUid(item.uid);
          if (
            block &&
            (block.tagName === "mj-text" || block.tagName === "mj-button")
          ) {
            block.content = item.content;
          }
        });
      } else {
        let translation = this.mjml.langs.find(
          (lang) => lang.key === this.langToShow
        );
        if (!translation) {
          const defaultLang = this.mjml.langs.find((lang) => lang.primary);
          if (!defaultLang) {
            this.mjml.langs[0].primary = true;
            translation = this.mjml.langs[0];
            this.emitLanguageCallback("onPrimaryLanguageChanged", [
              this.mjml.langs[0].key,
              langsToMutations(this.mjml.langs),
            ]);
          } else {
            translation = defaultLang;
          }
          this.langToShow = translation.key;
          this.emitLanguageCallback("onLanguageSelected", [this.langToShow]);
        }
        if (!translation.translations) {
          translation.translations = {};
        }
        translations.forEach((item) => {
          if (item.uid === "mj-preview" || item.uid === "mj-subject") {
            this.setBaseAttrTranslation({
              block: item.uid,
              prop: "text",
              value: item.content,
            });
            return;
          }
          const block = this.getSegmentByUid(item.uid);
          if (
            block &&
            (block.tagName === "mj-text" || block.tagName === "mj-button")
          ) {
            translation.translations[item.uid] = {
              content: item.content,
            };
          }
        });
      }
    },

    // Write AI translation results back into the template. Unlike
    // setTranslationOfWholeTemplate (content-only) this applies content AND
    // attributes / props / child attributes, MERGING into the existing
    // per-language translation object so unrelated fields (e.g. a manually set
    // link, or a sibling attribute we didn't translate) are preserved.
    // `units` are the ones produced by getTranslatableUnits; `translations` is
    // the endpoint response keyed by the synthetic unit id.
    applyTranslationUnits(
      units: TranslationUnit[],
      translations: { uid: string; content: string }[],
      targetLang: string | undefined,
      skipSnapshot: boolean
    ): void {
      if (!skipSnapshot) {
        this.takeSnapshot();
      }

      const byId = new Map<string, string>();
      for (const t of translations) byId.set(t.uid, t.content);

      this.edittedWithoutSave = true;

      const langs = this.mjml.langs;
      const hasLangs = !!langs?.length;

      // Resolve which mutation receives the translations. Mirrors the branching
      // in setTranslationOfWholeTemplate.
      let target: any | undefined;
      if (hasLangs) {
        if (targetLang) {
          target = langs!.find((lang) => lang.key === targetLang);
          if (!target) return;
        } else {
          target = langs!.find((lang) => lang.key === this.langToShow);
          if (!target) {
            const defaultLang = langs!.find((lang) => lang.primary);
            if (!defaultLang) {
              langs![0].primary = true;
              target = langs![0];
              this.emitLanguageCallback("onPrimaryLanguageChanged", [
                langs![0].key,
                langsToMutations(langs!),
              ]);
            } else {
              target = defaultLang;
            }
            this.langToShow = target.key;
            this.emitLanguageCallback("onLanguageSelected", [this.langToShow]);
          }
        }
        if (!target.translations) target.translations = {};
      }

      const ensureBlock = (uid: string): any => {
        if (!target.translations[uid]) target.translations[uid] = {};
        return target.translations[uid];
      };

      for (const unit of units) {
        const value = byId.get(unit.id);
        if (value === undefined) continue;
        const tgt = unit.target;

        // No mutations defined: write straight onto the live block / base attr.
        if (!hasLangs) {
          if (tgt.kind === "baseAttr") {
            this.setBaseAttrTranslation({
              block: tgt.block,
              prop: "text",
              value,
            });
            continue;
          }
          const block = this.getSegmentByUid(tgt.uid);
          if (!block) continue;
          if (tgt.kind === "content") {
            block.content = value;
          } else if (tgt.kind === "attr") {
            block.attributes = block.attributes || {};
            block.attributes[tgt.attr] = value;
          } else if (tgt.kind === "prop") {
            block[tgt.prop] = value;
          } else if (tgt.kind === "childAttr") {
            const child = block.children?.[tgt.childIndex];
            if (child) {
              child.attributes = child.attributes || {};
              child.attributes[tgt.attr] = value;
            }
          } else if (tgt.kind === "feedItem") {
            const item = block.items?.[tgt.itemIndex];
            if (item) {
              block.items[tgt.itemIndex] = { ...item, [tgt.field]: value };
            }
          }
          continue;
        }

        // Mutations exist: merge into target.translations[uid].
        if (tgt.kind === "baseAttr") {
          target.translations[tgt.block] = {
            ...target.translations[tgt.block],
            content: value,
          };
        } else if (tgt.kind === "content") {
          ensureBlock(tgt.uid).content = value;
        } else if (tgt.kind === "attr") {
          const blockTrans = ensureBlock(tgt.uid);
          blockTrans.attributes = {
            ...blockTrans.attributes,
            [tgt.attr]: value,
          };
        } else if (tgt.kind === "prop") {
          ensureBlock(tgt.uid)[tgt.prop] = value;
        } else if (tgt.kind === "childAttr") {
          const blockTrans = ensureBlock(tgt.uid);
          if (!Array.isArray(blockTrans.children)) blockTrans.children = [];
          if (!blockTrans.children[tgt.childIndex]) {
            blockTrans.children[tgt.childIndex] = {};
          }
          const child = blockTrans.children[tgt.childIndex];
          child.attributes = {
            ...child.attributes,
            [tgt.attr]: value,
          };
        } else if (tgt.kind === "feedItem") {
          // Feed items use the per-id sparse-override model. Translating into
          // the primary mutation would corrupt the authoritative item list, so
          // write straight into it; non-primary targets get a sparse override
          // keyed by the primary item's id (same as manual per-lang edits).
          const block = this.getSegmentByUid(tgt.uid);
          if (!block || block.tagName !== "mj-feed-block") continue;
          if (target.primary === true) {
            const primaryItems = this._resolvePrimaryFeedItems(block);
            if (primaryItems[tgt.itemIndex]) {
              primaryItems[tgt.itemIndex] = {
                ...primaryItems[tgt.itemIndex],
                [tgt.field]: value,
              };
            }
          } else {
            const blockTrans = ensureBlock(tgt.uid);
            blockTrans.items = this._upsertFeedItemFieldOverride(
              blockTrans.items,
              tgt.itemIndex,
              tgt.itemId,
              tgt.field,
              value
            );
          }
        }
      }
    },

    takeSnapshot(): any {
      if (this.snapshots.length === 10) {
        this.snapshots.pop();
        this.showLangSnapshots.pop();
      }

      this.snapshots.unshift(JSON.parse(JSON.stringify(this.mjml)));
      this.showLangSnapshots.unshift(this.langToShow);
      this.redoSnapshots = [];
    },

    toggleBlockLock(): any {
      const selectedBlock = this.getSelectedBlock;
      if (!selectedBlock) {
        return;
      }
      let newValue = false;
      const isLocked = selectedBlock.locked;

      if (isLocked === false || isLocked === undefined) {
        newValue = true;
      }

      this.updateBlockProp({ prop: "locked", value: newValue });
    },

    deleteSectionPromise(props: any): Promise<void> {
      return new Promise((resolve, reject) => {
        if (this.mjml.children[0].children.length > 1) {
          this.deleteSection(props);
          resolve();
        } else {
          reject();
        }
      });
    },

    throttledTakeSnapshot: useThrottleFn(function () {
      //@ts-expect-error TS thinks this is undefined but its not...
      this.takeSnapshot();
    }, 1000),

    debounceUpdateContainerWidthOnBlocks(containerWidth: number) {
      this.updateContainerWidthOnBlocks(containerWidth);
    },
  },
  // This is a pinia plugin that adds debounce to certains actions automatically
  // More here: https://github.com/posva/pinia-plugin-debounce
  debounce: {
    debounceUpdateContainerWidthOnBlocks: 500,
  },
});
